import React, { useState, useEffect, useRef } from "react";
import Chatbot from "../components/Chatbot";
import {
  FiFileText,
  FiDollarSign,
  FiShield,
  FiTrendingUp,
  FiAward,
  FiHeart,
  FiUsers,
  FiBriefcase,
  FiHome,
  FiArrowRight,
  FiArrowLeft,
  FiCheck,
  FiUser,
  FiMail,
  FiPhone,
  FiBook,
  FiStar,
  FiAlertCircle,
  FiLoader,
  FiChevronLeft,
  FiChevronRight,
  FiUpload,
  FiClock,
  FiMapPin,
  FiCalendar,
} from "react-icons/fi";
import { useLanguage } from "../context/LanguageContext";
import {
  submitFieldWorkerApplication,
  submitOfficeStaffApplication,
} from "../services/applicationService";
import "../styles/pages/Membership.css";
import "../styles/animations/ScrollAnimations.css";

// Import Field Service Worker hiring images
import carpetShampooing from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/CARPET SHAMPOOING.png";
import cashSorters from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/CASH SORTERS.png";
import cctvOperators from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/CCTV OPERATORS.png";
import conciergeServices from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/CONCIERGE SERVICES.png";
import constructionWorker from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/CONSTRUCTION WORKER.png";
import drivers from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/DRIVERS.png";
import janitor from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/JANITOR.png";
import messengers from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/MESSENGERS.png";
import officeTempClerks from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/OFFICE TEMP CLERKS.png";
import otherAlliedServices from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/OTHER ALLIED SERVICES.png";
import promotionMerchandisers from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/PROMOTIOIN MERCHENDISERS.png";
import radioOperators from "../images/MEDC HIRING PICTURES/FIELD SERVICE WORKERS/RADIO OPERATORS.png";

// Import Office Staff hiring images
import member201 from "../images/MEDC HIRING PICTURES/MEDC OFFICE EMPLOYEES/201 MEMBER.png";
import accountingStaff from "../images/MEDC HIRING PICTURES/MEDC OFFICE EMPLOYEES/ACCOUNTING STAFF.png";
import areaHead from "../images/MEDC HIRING PICTURES/MEDC OFFICE EMPLOYEES/AREA HEAD.png";
import hrStaff from "../images/MEDC HIRING PICTURES/MEDC OFFICE EMPLOYEES/HR STAFF.png";
import payrollBillingStaff from "../images/MEDC HIRING PICTURES/MEDC OFFICE EMPLOYEES/PAYROLL AND BILLING STAFF.png";

const Membership = () => {
  const { t } = useLanguage();
  const [selectedPath, setSelectedPath] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [formStep, setFormStep] = useState(1);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [resumeFile, setResumeFile] = useState(null);
  const [typingTestProofFile, setTypingTestProofFile] = useState(null);
  const [personalityTestProofFile, setPersonalityTestProofFile] =
    useState(null);
  const resumeInputRef = useRef(null);
  const [iqTestAnswers, setIqTestAnswers] = useState({});
  const [abstractTestAnswers, setAbstractTestAnswers] = useState({});
  const [showIqTestModal, setShowIqTestModal] = useState(false);
  const [showAbstractTestModal, setShowAbstractTestModal] = useState(false);
  const [showMathTestModal, setShowMathTestModal] = useState(false);
  const [showEssayTestModal, setShowEssayTestModal] = useState(false);
  const [showExternalTestModal, setShowExternalTestModal] = useState(false);
  const [showTypingTestModal, setShowTypingTestModal] = useState(false);
  const [showPersonalityTestModal, setShowPersonalityTestModal] =
    useState(false);
  const [showExamIntroModal, setShowExamIntroModal] = useState(false);

  // Math Analogy Test State Variables
  const [mathTestStatus, setMathTestStatus] = useState("idle"); // idle, running, completed
  const [mathTestAnswers, setMathTestAnswers] = useState({});
  const [mathTestTimeLeft, setMathTestTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  const [mathTestResults, setMathTestResults] = useState(null);
  const mathTestTimerRef = useRef(null);

  // Essay Writing Test State Variables
  const [essayTestStatus, setEssayTestStatus] = useState("idle"); // idle, running, completed
  const [essayContent, setEssayContent] = useState("");
  const [essayWordCount, setEssayWordCount] = useState(0);
  const [essayTimeLeft, setEssayTimeLeft] = useState(20 * 60); // 20 minutes in seconds
  const [showWordCountWarning, setShowWordCountWarning] = useState(false); // Warning modal for <200 words
  const [showEssayTimeoutModal, setShowEssayTimeoutModal] = useState(false); // Timeout notification modal
  const [essayTimedOut, setEssayTimedOut] = useState(false); // Track if essay timed out
  const essayTimerRef = useRef(null);

  // Excel Proficiency Test State Variables
  const [excelTestStatus, setExcelTestStatus] = useState("idle"); // idle, running, completed
  const [excelTimeLeft, setExcelTimeLeft] = useState(15 * 60); // 15 minutes in seconds
  const [excelFile, setExcelFile] = useState(null);
  const [showExcelTestModal, setShowExcelTestModal] = useState(false);
  const excelTimerRef = useRef(null);

  // Typing Test State Variables
  const [typingTestStatus, setTypingTestStatus] = useState("idle"); // idle, ready, running, completed
  const [typingTestText, setTypingTestText] = useState("");
  const [typingUserInput, setTypingUserInput] = useState("");
  const [typingTimeLeft, setTypingTimeLeft] = useState(60);
  const [typingStartTime, setTypingStartTime] = useState(null);
  const [typingResults, setTypingResults] = useState(null);
  const [animatedResults, setAnimatedResults] = useState({
    grossWpm: 0,
    accuracy: 0,
    netWpm: 0,
  });
  const [showResultsAnimation, setShowResultsAnimation] = useState(false);
  const [typingTimerStarted, setTypingTimerStarted] = useState(false);
  const typingInputRef = useRef(null);
  const typingTimerRef = useRef(null);
  const typingUserInputRef = useRef(""); // Track current input value

  // Personality Test (16PF) State Variables
  const [personalityTestStatus, setPersonalityTestStatus] = useState("idle"); // idle, running, completed
  const [personalityCurrentQuestion, setPersonalityCurrentQuestion] =
    useState(0);
  const [personalityAnswers, setPersonalityAnswers] = useState({});
  const [personalityResults, setPersonalityResults] = useState(null);
  const [showPersonalityResultsAnimation, setShowPersonalityResultsAnimation] =
    useState(false);

  // Sample typing test passages
  const typingTestPassages = [
    "The quick brown fox jumps over the lazy dog. This sentence contains every letter of the alphabet and is commonly used for typing practice. Good typing skills are essential in today's digital workplace. Practice makes perfect, and regular typing exercises can significantly improve your speed and accuracy. Remember to keep your fingers on the home row and maintain proper posture while typing.",
    "Technology has transformed the way we work and communicate. Computers and the internet have made it possible to connect with people around the world instantly. Email, video calls, and instant messaging are now standard tools in most offices. Learning to type efficiently is crucial for anyone working in a modern business environment. The ability to type quickly and accurately can save hours of work each week.",
    "Customer service is the backbone of any successful business. When dealing with clients, it is important to remain professional, courteous, and helpful at all times. Good communication skills are essential for understanding customer needs and providing appropriate solutions. Always listen carefully to what the customer is saying, and respond in a clear and friendly manner. Building strong relationships with customers leads to repeat business and positive referrals.",
    "Time management is a valuable skill that can help you achieve your goals more efficiently. Start by prioritizing your tasks based on importance and deadlines. Break large projects into smaller, manageable steps and set realistic timelines for completion. Avoid distractions and stay focused on the task at hand. Taking regular breaks can help maintain productivity and prevent burnout. Remember that effective time management is about working smarter, not harder.",
    "The modern workplace requires employees to be adaptable and willing to learn new skills. As technology continues to evolve, the ability to quickly acquire new knowledge becomes increasingly important. Employers value workers who can think critically, solve problems creatively, and collaborate effectively with others. Continuous learning and professional development are essential for career growth and job satisfaction in today's competitive job market.",
  ];

  // Cattell's 16 Personality Factors (16PF) Assessment
  // Based on Raymond Cattell's factor analysis research
  // Uses IPIP (International Personality Item Pool) public domain scales

  // The 16 Primary Factors
  const personalityFactors = {
    A: {
      name: "Warmth",
      lowLabel: "Reserved",
      highLabel: "Warm",
      lowDescription:
        "Impersonal, distant, cool, reserved, detached, formal, aloof",
      highDescription:
        "Warm, outgoing, attentive to others, kindly, easy-going, participating, likes people",
      color: "#4298b4",
    },
    B: {
      name: "Reasoning",
      lowLabel: "Concrete",
      highLabel: "Abstract",
      lowDescription:
        "Concrete thinking, lower general mental capacity, less intelligent",
      highDescription:
        "Abstract-thinking, more intelligent, bright, higher general mental capacity",
      color: "#88619a",
    },
    C: {
      name: "Emotional Stability",
      lowLabel: "Reactive",
      highLabel: "Emotionally Stable",
      lowDescription:
        "Reactive emotionally, changeable, affected by feelings, easily upset",
      highDescription:
        "Emotionally stable, adaptive, mature, faces reality calmly",
      color: "#33a474",
    },
    E: {
      name: "Dominance",
      lowLabel: "Deferential",
      highLabel: "Dominant",
      lowDescription:
        "Deferential, cooperative, avoids conflict, submissive, humble",
      highDescription:
        "Dominant, forceful, assertive, aggressive, competitive, stubborn",
      color: "#e4ae3a",
    },
    F: {
      name: "Liveliness",
      lowLabel: "Serious",
      highLabel: "Lively",
      lowDescription: "Serious, restrained, prudent, taciturn, introspective",
      highDescription:
        "Lively, animated, spontaneous, enthusiastic, happy-go-lucky",
      color: "#f59e0b",
    },
    G: {
      name: "Rule-Consciousness",
      lowLabel: "Expedient",
      highLabel: "Rule-Conscious",
      lowDescription:
        "Expedient, nonconforming, disregards rules, self-indulgent",
      highDescription:
        "Rule-conscious, dutiful, conscientious, conforming, moralistic",
      color: "#4298b4",
    },
    H: {
      name: "Social Boldness",
      lowLabel: "Shy",
      highLabel: "Socially Bold",
      lowDescription: "Shy, threat-sensitive, timid, hesitant, intimidated",
      highDescription: "Socially bold, venturesome, thick-skinned, uninhibited",
      color: "#ef4444",
    },
    I: {
      name: "Sensitivity",
      lowLabel: "Utilitarian",
      highLabel: "Sensitive",
      lowDescription:
        "Utilitarian, objective, unsentimental, tough-minded, self-reliant",
      highDescription:
        "Sensitive, aesthetic, sentimental, tender-minded, intuitive",
      color: "#ec4899",
    },
    L: {
      name: "Vigilance",
      lowLabel: "Trusting",
      highLabel: "Vigilant",
      lowDescription: "Trusting, unsuspecting, accepting, unconditional, easy",
      highDescription:
        "Vigilant, suspicious, skeptical, distrustful, oppositional",
      color: "#6366f1",
    },
    M: {
      name: "Abstractedness",
      lowLabel: "Grounded",
      highLabel: "Abstracted",
      lowDescription: "Grounded, practical, prosaic, solution-oriented, steady",
      highDescription:
        "Abstracted, imaginative, absent-minded, impractical, absorbed in ideas",
      color: "#8b5cf6",
    },
    N: {
      name: "Privateness",
      lowLabel: "Forthright",
      highLabel: "Private",
      lowDescription: "Forthright, genuine, artless, open, guileless, naive",
      highDescription:
        "Private, discreet, nondisclosing, shrewd, polished, worldly",
      color: "#64748b",
    },
    O: {
      name: "Apprehension",
      lowLabel: "Self-Assured",
      highLabel: "Apprehensive",
      lowDescription:
        "Self-assured, unworried, complacent, secure, free of guilt",
      highDescription:
        "Apprehensive, self-doubting, worried, guilt-prone, insecure",
      color: "#78716c",
    },
    Q1: {
      name: "Openness to Change",
      lowLabel: "Traditional",
      highLabel: "Open to Change",
      lowDescription:
        "Traditional, attached to familiar, conservative, respecting traditional ideas",
      highDescription:
        "Open to change, experimenting, liberal, analytical, critical, freethinking",
      color: "#06b6d4",
    },
    Q2: {
      name: "Self-Reliance",
      lowLabel: "Group-Oriented",
      highLabel: "Self-Reliant",
      lowDescription:
        "Group-oriented, affiliative, a joiner and follower dependent",
      highDescription:
        "Self-reliant, solitary, resourceful, individualistic, self-sufficient",
      color: "#10b981",
    },
    Q3: {
      name: "Perfectionism",
      lowLabel: "Tolerates Disorder",
      highLabel: "Perfectionistic",
      lowDescription:
        "Tolerates disorder, unexacting, flexible, undisciplined, lax",
      highDescription:
        "Perfectionistic, organized, compulsive, self-disciplined, socially precise",
      color: "#3b82f6",
    },
    Q4: {
      name: "Tension",
      lowLabel: "Relaxed",
      highLabel: "Tense",
      lowDescription: "Relaxed, placid, tranquil, torpid, patient, composed",
      highDescription:
        "Tense, high energy, impatient, driven, frustrated, overwrought",
      color: "#f97316",
    },
  };

  // 164 Questions for Cattell's 16PF Assessment (IPIP version)
  // Each question maps to one of the 16 factors
  // keyed: '+' means agreeing increases the factor score, '-' means agreeing decreases it
  const personalityQuestions = [
    // Questions 1-10
    { id: 1, text: "I take time out for others.", factor: "A", keyed: "+" },
    {
      id: 2,
      text: "I know that I am not a special person.",
      factor: "E",
      keyed: "-",
    },
    { id: 3, text: "I take control of things.", factor: "E", keyed: "+" },
    { id: 4, text: "I try to forgive and forget.", factor: "L", keyed: "-" },
    { id: 5, text: "I keep in the background.", factor: "H", keyed: "-" },
    {
      id: 6,
      text: "I can't do without the company of others.",
      factor: "Q2",
      keyed: "-",
    },
    { id: 7, text: "I trust others.", factor: "L", keyed: "-" },
    { id: 8, text: "I am not easily frustrated.", factor: "Q4", keyed: "-" },
    { id: 9, text: "I cheer people up.", factor: "F", keyed: "+" },
    {
      id: 10,
      text: "I often feel uncomfortable around others.",
      factor: "H",
      keyed: "-",
    },

    // Questions 11-20
    { id: 11, text: "I seldom feel blue.", factor: "O", keyed: "-" },
    { id: 12, text: "I dislike myself.", factor: "C", keyed: "-" },
    { id: 13, text: "I take charge.", factor: "E", keyed: "+" },
    {
      id: 14,
      text: "I let others make the decisions.",
      factor: "E",
      keyed: "-",
    },
    {
      id: 15,
      text: "I believe in the importance of art.",
      factor: "I",
      keyed: "+",
    },
    { id: 16, text: "I like to get lost in thought.", factor: "M", keyed: "+" },
    {
      id: 17,
      text: "I wait for others to lead the way.",
      factor: "E",
      keyed: "-",
    },
    {
      id: 18,
      text: "I am willing to talk about myself.",
      factor: "N",
      keyed: "-",
    },
    {
      id: 19,
      text: "I find it difficult to approach others.",
      factor: "H",
      keyed: "-",
    },
    { id: 20, text: "I enjoy my privacy.", factor: "Q2", keyed: "+" },

    // Questions 21-30
    { id: 21, text: "I swim against the current.", factor: "Q1", keyed: "+" },
    { id: 22, text: 'I feel guilty when I say "no."', factor: "O", keyed: "+" },
    { id: 23, text: "I am hard to get to know.", factor: "N", keyed: "+" },
    { id: 24, text: "I don't talk a lot.", factor: "H", keyed: "-" },
    {
      id: 25,
      text: "I believe in one true religion.",
      factor: "Q1",
      keyed: "-",
    },
    { id: 26, text: "I am not easily annoyed.", factor: "L", keyed: "-" },
    { id: 27, text: "I feel crushed by setbacks.", factor: "C", keyed: "-" },
    {
      id: 28,
      text: "I am afraid that I will do the wrong thing.",
      factor: "O",
      keyed: "+",
    },
    {
      id: 29,
      text: "I enjoy being part of a loud crowd.",
      factor: "F",
      keyed: "+",
    },
    {
      id: 30,
      text: "I weigh the pros against the cons.",
      factor: "B",
      keyed: "+",
    },

    // Questions 31-40
    { id: 31, text: "I do unexpected things.", factor: "F", keyed: "+" },
    { id: 32, text: "I get angry easily.", factor: "Q4", keyed: "+" },
    { id: 33, text: "I am quiet around strangers.", factor: "H", keyed: "-" },
    { id: 34, text: "I don't mind eating alone.", factor: "Q2", keyed: "+" },
    { id: 35, text: "I make people feel at ease.", factor: "A", keyed: "+" },
    { id: 36, text: "I use my brain.", factor: "B", keyed: "+" },
    {
      id: 37,
      text: "I have a good word for everyone.",
      factor: "A",
      keyed: "+",
    },
    { id: 38, text: "I feel desperate.", factor: "C", keyed: "-" },
    { id: 39, text: "I want to be in charge.", factor: "E", keyed: "+" },
    {
      id: 40,
      text: "I feel comfortable around people.",
      factor: "H",
      keyed: "+",
    },

    // Questions 41-50
    { id: 41, text: "I am the life of the party.", factor: "F", keyed: "+" },
    {
      id: 42,
      text: "I don't let others discourage me.",
      factor: "C",
      keyed: "+",
    },
    {
      id: 43,
      text: "I enjoy being part of a group.",
      factor: "Q2",
      keyed: "-",
    },
    { id: 44, text: "I love to daydream.", factor: "M", keyed: "+" },
    { id: 45, text: "I distrust people.", factor: "L", keyed: "+" },
    { id: 46, text: "I worry about things.", factor: "O", keyed: "+" },
    {
      id: 47,
      text: "I am not easily bothered by things.",
      factor: "C",
      keyed: "+",
    },
    { id: 48, text: "I respect authority.", factor: "G", keyed: "+" },
    {
      id: 49,
      text: "I do things that others find strange.",
      factor: "Q1",
      keyed: "+",
    },
    {
      id: 50,
      text: "I skip difficult words while reading.",
      factor: "B",
      keyed: "-",
    },

    // Questions 51-60
    {
      id: 51,
      text: "I feel comfortable with myself.",
      factor: "C",
      keyed: "+",
    },
    { id: 52, text: "I am exacting in my work.", factor: "Q3", keyed: "+" },
    { id: 53, text: "I tend to analyze things.", factor: "B", keyed: "+" },
    {
      id: 54,
      text: "I continue until everything is perfect.",
      factor: "Q3",
      keyed: "+",
    },
    {
      id: 55,
      text: "I believe that people are basically moral.",
      factor: "L",
      keyed: "-",
    },
    { id: 56, text: "I am quick to judge others.", factor: "L", keyed: "+" },
    {
      id: 57,
      text: "I am relaxed most of the time.",
      factor: "Q4",
      keyed: "-",
    },
    { id: 58, text: "I enjoy silence.", factor: "Q2", keyed: "+" },
    { id: 59, text: "I show my feelings.", factor: "N", keyed: "-" },
    {
      id: 60,
      text: "I judge people by their appearance.",
      factor: "L",
      keyed: "+",
    },

    // Questions 61-70
    { id: 61, text: "I prefer variety to routine.", factor: "Q1", keyed: "+" },
    { id: 62, text: "I never challenge things.", factor: "E", keyed: "-" },
    {
      id: 63,
      text: "I can't stand being contradicted.",
      factor: "Q4",
      keyed: "+",
    },
    {
      id: 64,
      text: "I try not to think about the needy.",
      factor: "A",
      keyed: "-",
    },
    { id: 65, text: "I am easily put out.", factor: "Q4", keyed: "+" },
    {
      id: 66,
      text: "I prefer to do things by myself.",
      factor: "Q2",
      keyed: "+",
    },
    { id: 67, text: "I get irritated easily.", factor: "Q4", keyed: "+" },
    {
      id: 68,
      text: "I know the answers to many questions.",
      factor: "B",
      keyed: "+",
    },
    { id: 69, text: "I trust what people say.", factor: "L", keyed: "-" },
    {
      id: 70,
      text: "I like to stand during the national anthem.",
      factor: "G",
      keyed: "+",
    },

    // Questions 71-80
    { id: 71, text: "I love flowers.", factor: "I", keyed: "+" },
    {
      id: 72,
      text: "I find it hard to forgive others.",
      factor: "L",
      keyed: "+",
    },
    { id: 73, text: "I leave my belongings around.", factor: "Q3", keyed: "-" },
    { id: 74, text: "I feel others' emotions.", factor: "I", keyed: "+" },
    { id: 75, text: "I let myself be pushed around.", factor: "E", keyed: "-" },
    { id: 76, text: "I don't like crowded events.", factor: "H", keyed: "-" },
    { id: 77, text: "I enjoy hearing new ideas.", factor: "Q1", keyed: "+" },
    { id: 78, text: "I act wild and crazy.", factor: "F", keyed: "+" },
    { id: 79, text: "I read a lot.", factor: "B", keyed: "+" },
    { id: 80, text: "I try to follow the rules.", factor: "G", keyed: "+" },

    // Questions 81-90
    {
      id: 81,
      text: "I enjoy wild flights of fantasy.",
      factor: "M",
      keyed: "+",
    },
    { id: 82, text: "I use swear words.", factor: "G", keyed: "-" },
    {
      id: 83,
      text: "I don't worry about things that have already happened.",
      factor: "O",
      keyed: "-",
    },
    { id: 84, text: "I say what I think.", factor: "N", keyed: "-" },
    { id: 85, text: "I am easily hurt.", factor: "I", keyed: "+" },
    {
      id: 86,
      text: "I enjoy spending time by myself.",
      factor: "Q2",
      keyed: "+",
    },
    {
      id: 87,
      text: "I don't mind being the center of attention.",
      factor: "H",
      keyed: "+",
    },
    { id: 88, text: "I seldom get lost in thought.", factor: "M", keyed: "-" },
    { id: 89, text: "I seldom daydream.", factor: "M", keyed: "-" },
    {
      id: 90,
      text: "I suspect hidden motives in others.",
      factor: "L",
      keyed: "+",
    },

    // Questions 91-100
    {
      id: 91,
      text: "I am not interested in abstract ideas.",
      factor: "M",
      keyed: "-",
    },
    { id: 92, text: "I am easily discouraged.", factor: "C", keyed: "-" },
    {
      id: 93,
      text: "I am not afraid of providing criticism.",
      factor: "E",
      keyed: "+",
    },
    {
      id: 94,
      text: "I disclose my intimate thoughts.",
      factor: "N",
      keyed: "-",
    },
    { id: 95, text: "I don't like action movies.", factor: "I", keyed: "+" },
    {
      id: 96,
      text: 'I want everything to be "just right."',
      factor: "Q3",
      keyed: "+",
    },
    { id: 97, text: "I feel threatened easily.", factor: "O", keyed: "+" },
    {
      id: 98,
      text: "I am the last to laugh at a joke.",
      factor: "F",
      keyed: "-",
    },
    {
      id: 99,
      text: "I enjoy discussing movies and books with others.",
      factor: "A",
      keyed: "+",
    },
    { id: 100, text: "I joke around a lot.", factor: "F", keyed: "+" },

    // Questions 101-110
    { id: 101, text: "I have a poor vocabulary.", factor: "B", keyed: "-" },
    { id: 102, text: "I dislike loud music.", factor: "I", keyed: "+" },
    { id: 103, text: "I make insightful remarks.", factor: "B", keyed: "+" },
    {
      id: 104,
      text: "I enjoy bringing people together.",
      factor: "A",
      keyed: "+",
    },
    {
      id: 105,
      text: "I get chores done right away.",
      factor: "Q3",
      keyed: "+",
    },
    {
      id: 106,
      text: "I reflect on things before acting.",
      factor: "F",
      keyed: "-",
    },
    {
      id: 107,
      text: "I am not bothered by disorder.",
      factor: "Q3",
      keyed: "-",
    },
    {
      id: 108,
      text: "I don't like to get involved in other people's problems.",
      factor: "A",
      keyed: "-",
    },
    { id: 109, text: "I break rules.", factor: "G", keyed: "-" },
    { id: 110, text: "I can take strong measures.", factor: "E", keyed: "+" },

    // Questions 111-120
    { id: 111, text: "I love large parties.", factor: "H", keyed: "+" },
    { id: 112, text: "I do not like poetry.", factor: "I", keyed: "-" },
    {
      id: 113,
      text: "I believe that others have good intentions.",
      factor: "L",
      keyed: "-",
    },
    { id: 114, text: "I leave a mess in my room.", factor: "Q3", keyed: "-" },
    { id: 115, text: "I put off unpleasant tasks.", factor: "Q3", keyed: "-" },
    { id: 116, text: "I oppose authority.", factor: "G", keyed: "-" },
    { id: 117, text: "I resist authority.", factor: "G", keyed: "-" },
    { id: 118, text: "I readily overcome setbacks.", factor: "C", keyed: "+" },
    { id: 119, text: "I get confused easily.", factor: "B", keyed: "-" },
    { id: 120, text: "I know how to comfort others.", factor: "A", keyed: "+" },

    // Questions 121-130
    {
      id: 121,
      text: "I am open about myself to others.",
      factor: "N",
      keyed: "-",
    },
    {
      id: 122,
      text: "I rarely notice my emotional reactions.",
      factor: "I",
      keyed: "-",
    },
    { id: 123, text: "I amuse my friends.", factor: "F", keyed: "+" },
    {
      id: 124,
      text: "I love to think up new ways of doing things.",
      factor: "Q1",
      keyed: "+",
    },
    { id: 125, text: "I dislike works of fiction.", factor: "M", keyed: "-" },
    {
      id: 126,
      text: "I do not enjoy watching dance performances.",
      factor: "I",
      keyed: "-",
    },
    { id: 127, text: "I start conversations.", factor: "H", keyed: "+" },
    { id: 128, text: "I make friends easily.", factor: "H", keyed: "+" },
    { id: 129, text: "I often feel blue.", factor: "O", keyed: "+" },
    { id: 130, text: "I counter others' arguments.", factor: "E", keyed: "+" },

    // Questions 131-140
    {
      id: 131,
      text: "I am not interested in theoretical discussions.",
      factor: "M",
      keyed: "-",
    },
    { id: 132, text: "I seek quiet.", factor: "Q2", keyed: "+" },
    { id: 133, text: "I have frequent mood swings.", factor: "C", keyed: "-" },
    { id: 134, text: "I learn quickly.", factor: "B", keyed: "+" },
    {
      id: 135,
      text: "I rarely look for a deeper meaning in things.",
      factor: "M",
      keyed: "-",
    },
    { id: 136, text: "I like to read.", factor: "B", keyed: "+" },
    { id: 137, text: "I keep my thoughts to myself.", factor: "N", keyed: "+" },
    {
      id: 138,
      text: "I try to avoid complex people.",
      factor: "A",
      keyed: "-",
    },
    { id: 139, text: "I reveal little about myself.", factor: "N", keyed: "+" },
    {
      id: 140,
      text: "I am not bothered by messy people.",
      factor: "Q3",
      keyed: "-",
    },

    // Questions 141-150
    {
      id: 141,
      text: "I consider myself an average person.",
      factor: "E",
      keyed: "-",
    },
    { id: 142, text: "I like order.", factor: "Q3", keyed: "+" },
    {
      id: 143,
      text: "I avoid philosophical discussions.",
      factor: "M",
      keyed: "-",
    },
    {
      id: 144,
      text: "I am annoyed by others' mistakes.",
      factor: "L",
      keyed: "+",
    },
    { id: 145, text: "I cry during movies.", factor: "I", keyed: "+" },
    {
      id: 146,
      text: "I am not really interested in others.",
      factor: "A",
      keyed: "-",
    },
    {
      id: 147,
      text: "I believe that people are essentially evil.",
      factor: "L",
      keyed: "+",
    },
    {
      id: 148,
      text: "I know how to get around the rules.",
      factor: "G",
      keyed: "-",
    },
    { id: 149, text: "I seldom joke around.", factor: "F", keyed: "-" },
    {
      id: 150,
      text: "I carry the conversation to a higher level.",
      factor: "B",
      keyed: "+",
    },

    // Questions 151-160
    {
      id: 151,
      text: "I spend time thinking about past mistakes.",
      factor: "O",
      keyed: "+",
    },
    {
      id: 152,
      text: "I talk to a lot of different people at parties.",
      factor: "H",
      keyed: "+",
    },
    { id: 153, text: "I bottle up my feelings.", factor: "N", keyed: "+" },
    { id: 154, text: "I want to be left alone.", factor: "Q2", keyed: "+" },
    {
      id: 155,
      text: "I take an interest in other people's lives.",
      factor: "A",
      keyed: "+",
    },
    { id: 156, text: "I am wary of others.", factor: "L", keyed: "+" },
    { id: 157, text: "I enjoy teamwork.", factor: "Q2", keyed: "-" },
    { id: 158, text: "I have little to say.", factor: "H", keyed: "-" },
    {
      id: 159,
      text: "I believe laws should be strictly enforced.",
      factor: "G",
      keyed: "+",
    },
    { id: 160, text: "I do things by the book.", factor: "G", keyed: "+" },

    // Questions 161-164
    { id: 161, text: "I am open about my feelings.", factor: "N", keyed: "-" },
    {
      id: 162,
      text: "I believe that people seldom tell you the whole truth.",
      factor: "L",
      keyed: "+",
    },
    { id: 163, text: "I take deviant positions.", factor: "Q1", keyed: "+" },
    {
      id: 164,
      text: "I am comfortable in unfamiliar situations.",
      factor: "C",
      keyed: "+",
    },
  ];

  // MEDC IQ Test Questions
  const iqTestQuestions = [
    {
      id: 1,
      question: "If he _____ the answer, he would have told us.",
      options: ["knows", "knew", "had known"],
      correct: 2,
    },
    {
      id: 2,
      question: "By the time they arrive, we _____ dinner.",
      options: ["will have finished", "finished", "will finish"],
      correct: 0,
    },
    {
      id: 3,
      question: "What is the value of 7² + 5 × 3?",
      options: ["64", "56", "49"],
      correct: 0,
    },
    {
      id: 4,
      question: "I wanted to go swimming, _____ it started raining.",
      options: ["but", "because", "even"],
      correct: 0,
    },
    {
      id: 5,
      question:
        "If a rectangle has a length of 10 cm and a width of 5 cm, what is its area?",
      options: ["15 cm²", "50 cm²", "25 cm²"],
      correct: 1,
    },
    {
      id: 6,
      question: "Which shape logically completes the sequence? ●, ◯, ●, ◯, __?",
      options: ["●", "◯", "★"],
      correct: 0,
    },
    {
      id: 7,
      question:
        "If all squares are rectangles, and all rectangles are parallelograms, which of the following is true?",
      options: [
        "All squares are parallelograms.",
        "No square is a parallelogram.",
        "All parallelograms are squares.",
      ],
      correct: 0,
    },
    {
      id: 8,
      question: "Solve for x: 2x - 4 = 12",
      options: ["x = 8", "x = 6", "x = 5"],
      correct: 0,
    },
    {
      id: 9,
      question:
        "You have been given a task with a tight deadline, and you have several other tasks to complete. What is the best approach?",
      options: [
        "Complete the tasks in the order you receive them, regardless of deadlines.",
        "Prioritize the tasks based on deadlines and importance, and ask for help if needed.",
        "Focus on one task and complete it before starting another, even if it means missing deadlines.",
      ],
      correct: 1,
    },
    {
      id: 10,
      question:
        "You receive an email from a coworker with a request that you are unsure how to handle. What should you do?",
      options: [
        "Ignore the email and hope someone else responds.",
        "Immediately forward the email to your supervisor without addressing it.",
        "Reply to the coworker, asking for clarification or further information if necessary, and then proceed with the task.",
      ],
      correct: 2,
    },
    {
      id: 11,
      question: "Which number completes the sequence: 1, 4, 9, 16, __?",
      options: ["25", "20", "22"],
      correct: 0,
    },
    {
      id: 12,
      question:
        "If a company buys office supplies on credit, how will this transaction affect the accounting equation?",
      options: [
        "Assets increase, liabilities decrease.",
        "Assets decrease, liabilities increase.",
        "Assets increase, liabilities increase.",
      ],
      correct: 2,
    },
    {
      id: 13,
      question: "Which of the following is an example of a liability?",
      options: ["Cash", "Accounts Receivable", "Accounts Payable"],
      correct: 2,
    },
    {
      id: 14,
      question:
        "If an employee works 8 hours a day but has an additional 1 hour of commuting time, and the company does not pay for commuting time, how many hours should be reported for payroll?",
      options: ["9 hours", "8 hours", "7 hours"],
      correct: 1,
    },
    {
      id: 15,
      question:
        "A customer is upset because their order was delayed. How should you handle the situation?",
      options: [
        "Apologize for the delay, explain the cause, and offer a solution or compensation.",
        "Ignore the customer's complaint and continue with your work.",
        "Tell the customer it's not your fault and redirect them to someone else.",
      ],
      correct: 0,
    },
    {
      id: 16,
      question:
        "How should you respond if a customer asks for a product that is out of stock?",
      options: [
        "Tell the customer you cannot help them and end the conversation.",
        "Apologize for the inconvenience, offer alternative products, and let them know when the item will be back in stock.",
        "Ignore the customer's request and direct them to a different department.",
      ],
      correct: 1,
    },
    {
      id: 17,
      question:
        "If you find yourself distracted during work, what is the best way to get back on track?",
      options: [
        "Ignore the distractions and keep working without taking any breaks.",
        "Take a short break to clear your mind and then return to your tasks with focus.",
        "Continue working on something else until you feel less distracted.",
      ],
      correct: 1,
    },
    {
      id: 18,
      question:
        "Which financial statement shows the company's profitability over a specific period of time?",
      options: ["Balance Sheet", "Statement of Cash Flows", "Income Statement"],
      correct: 2,
    },
    {
      id: 19,
      question:
        "As an Operations Manager, you notice a decline in the team's productivity. What is the first step you should take to address the issue?",
      options: [
        "Immediately implement stricter rules and monitor employees closely.",
        "Hold a team meeting to discuss the issue, gather feedback, and identify potential causes.",
        "Assign more tasks to the team to ensure they stay busy.",
      ],
      correct: 1,
    },
    {
      id: 20,
      question:
        "When faced with a challenging situation at work, how do you typically respond?",
      options: [
        "I stay calm, assess the situation, and look for the best solution while involving relevant team members if needed.",
        "I focus on the problem until I can figure out a way to fix it myself, without asking for help.",
        "I avoid the situation and hope someone else will handle it.",
      ],
      correct: 0,
    },
    {
      id: 21,
      question: "Which sentence is correct?",
      options: [
        "She can sings very well.",
        "She can sing very well.",
        "She can singing very well.",
      ],
      correct: 1,
    },
    {
      id: 22,
      question:
        "Which of the following is used to open a file or program on a computer?",
      options: ["Right-click", "Double-click", "Drag and drop"],
      correct: 1,
    },
    {
      id: 23,
      question:
        "In the payroll process, if an employee works overtime, how should the overtime pay typically be calculated?",
      options: [
        "Overtime pay is calculated at the same rate as regular hours.",
        "Overtime pay is calculated at 1.5 times the regular hourly rate.",
        "Overtime pay is calculated at double the regular hourly rate.",
      ],
      correct: 1,
    },
    {
      id: 24,
      question:
        "How should a manager address a situation where a staff member is frequently missing deadlines?",
      options: [
        "Ignore the issue and hope the staff member improves over time.",
        "Have a one-on-one meeting with the staff member to discuss the issue and understand the reasons behind the missed deadlines.",
        "Immediately reprimand the staff member in front of the team.",
      ],
      correct: 1,
    },
    {
      id: 25,
      question:
        'Which of the following words most closely aligns with the meaning of "antediluvian"?',
      options: ["Modern", "Ancient", "Eccentric"],
      correct: 1,
    },
  ];

  const shuffleQuestions = (questions) => {
    const shuffled = [...questions];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const randomIndex = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[randomIndex]] = [
        shuffled[randomIndex],
        shuffled[i],
      ];
    }
    return shuffled;
  };

  const [iqTestAttemptQuestions, setIqTestAttemptQuestions] =
    useState(iqTestQuestions);

  const abstractReasoningQuestions = [
    {
      id: 1,
      question:
        "Which shape completes the sequence? Sequence: 🔵, 🟡, 🔺, 🔵, 🟡, _?",
      options: ["🔺", "🔵", "🟡"],
      correct: 0,
    },
    {
      id: 2,
      question:
        "Which of the following shapes completes the pattern? Sequence: ⬛, 🟨, ⬛, 🟨, _?",
      options: ["🟨", "🟥", "⬛"],
      correct: 2,
    },
    {
      id: 3,
      question:
        "What is the next shape in the sequence? Sequence: 🔶, 🔷, 🔶, 🔷, _?",
      options: ["🔶", "🔲", "🔷"],
      correct: 0,
    },
    {
      id: 4,
      question: "Which shape is the odd one out?",
      options: ["🔺", "🟨", "🔵"],
      correct: 1,
    },
    {
      id: 5,
      question:
        "Choose the correct shape to fit into the blank space. Sequence: ⬛, ◯, 🔺, ⬛, ◯, _?",
      options: ["◯", "🔺", "🟨"],
      correct: 1,
    },
    {
      id: 6,
      question:
        "Which shape completes the sequence? Sequence: 🔺, 🔲, 🔵, 🔺, 🔲, _?",
      options: ["🔵", "🔺", "🔲"],
      correct: 0,
    },
    {
      id: 7,
      question:
        "Which of the following shapes completes the pattern? Sequence: ◯, 🔷, ⬛, ◯, 🔷, _?",
      options: ["⬛", "◯", "🔶"],
      correct: 0,
    },
    {
      id: 8,
      question:
        "What is the next shape in the sequence? Sequence: 🔺, ⬛, 🔵, ⬛, 🔺, _?",
      options: ["🔵", "⬛", "🔺"],
      correct: 1,
    },
    {
      id: 9,
      question: "Which shape is the odd one out?",
      options: ["🟩", "🔶", "◯"],
      correct: 2,
    },
    {
      id: 10,
      question:
        "Choose the correct shape to fit into the blank space. Sequence: 🔲, 🔺, ⬛, 🔲, 🔺, _?",
      options: ["⬛", "🔺", "🔲"],
      correct: 0,
    },
    {
      id: 11,
      question:
        "Which shape completes the sequence? Sequence: 🔶, ◯, 🔶, ◯, _?",
      options: ["🔶", "⬛", "🔷"],
      correct: 0,
    },
    {
      id: 12,
      question:
        "What is the next shape in the sequence? Sequence: 🔶, 🟩, 🔷, 🟩, _?",
      options: ["🔶", "🔷", "🟩"],
      correct: 1,
    },
    {
      id: 13,
      question: "Which of the following is the odd one out?",
      options: ["◯", "🔶", "🔷"],
      correct: 0,
    },
    {
      id: 14,
      question:
        "What comes next in the sequence? Sequence: ⬛, 🔵, ◯, ⬛, 🔵, _?",
      options: ["◯", "🔺", "🟡"],
      correct: 0,
    },
    {
      id: 15,
      question:
        "Choose the shape that completes the sequence: Sequence: 🔵, 🟠, ⬛, 🔵, 🟠, _?",
      options: ["🔵", "⬛", "🟠"],
      correct: 1,
    },
  ];

  const [abstractAttemptQuestions, setAbstractAttemptQuestions] = useState(
    abstractReasoningQuestions,
  );

  // Mathematical Analogy Test - Time Conversion Questions
  const mathTimeQuestions = [
    {
      id: 1,
      timeIn: "8:15am",
      timeOut: "5:45pm",
      correctHours: 9,
      correctMinutes: 30,
      totalMinutes: 570,
    },
    {
      id: 2,
      timeIn: "7:30am",
      timeOut: "6:15pm",
      correctHours: 10,
      correctMinutes: 45,
      totalMinutes: 645,
    },
    {
      id: 3,
      timeIn: "8:30am",
      timeOut: "9:45pm",
      correctHours: 13,
      correctMinutes: 15,
      totalMinutes: 795,
    },
    {
      id: 4,
      timeIn: "8:00am",
      timeOut: "5:45pm",
      correctHours: 9,
      correctMinutes: 45,
      totalMinutes: 585,
    },
    {
      id: 5,
      timeIn: "8:25am",
      timeOut: "6:15pm",
      correctHours: 9,
      correctMinutes: 50,
      totalMinutes: 590,
    },
  ];

  // Carousel states
  const [activeFieldSlide, setActiveFieldSlide] = useState(0);
  const [activeOfficeSlide, setActiveOfficeSlide] = useState(0);
  const [isFieldCarouselPaused, setIsFieldCarouselPaused] = useState(false);
  const [isOfficeCarouselPaused, setIsOfficeCarouselPaused] = useState(false);
  const lastFieldInteractionRef = useRef(Date.now());
  const lastOfficeInteractionRef = useRef(Date.now());

  // Field Service Worker positions with images
  const fieldServicePositions = [
    {
      id: "janitor",
      title: "Janitorial Staff",
      image: janitor,
      slots: 15,
      location: "Nationwide",
      status: "hiring",
      description:
        "Maintain cleanliness and orderliness of office premises, buildings, and facilities. Responsible for sweeping, mopping, dusting, and waste disposal.",
    },
    {
      id: "messenger",
      title: "Messenger/Collector",
      image: messengers,
      slots: 10,
      location: "Metro Manila, Cebu, Davao",
      status: "hiring",
      description:
        "Handle document delivery, collection of payments, and coordination between offices. Must be familiar with local routes and have good communication skills.",
    },
    {
      id: "driver",
      title: "Company Drivers",
      image: drivers,
      slots: 8,
      location: "Luzon, Visayas, Mindanao",
      status: "hiring",
      description:
        "Operate company vehicles for transporting personnel, documents, and goods. Must have valid professional driver's license and clean driving record.",
    },
    {
      id: "construction",
      title: "Construction Workers",
      image: constructionWorker,
      slots: 25,
      location: "Nationwide",
      status: "hiring",
      description:
        "Perform various construction tasks including masonry, carpentry, painting, and general labor. Experience in construction projects preferred.",
    },
    {
      id: "carpet",
      title: "Carpet Shampooing Staff",
      image: carpetShampooing,
      slots: 5,
      location: "Metro Manila, Cebu",
      status: "hiring",
      description:
        "Specialize in deep cleaning carpets, upholstery, and fabric materials using professional equipment. Training will be provided.",
    },
    {
      id: "cashsorter",
      title: "Cash Sorters",
      image: cashSorters,
      slots: 6,
      location: "Davao, Cebu",
      status: "hiring",
      description:
        "Sort, count, and verify cash transactions with accuracy. Work in banking or financial institutions. Must be detail-oriented and trustworthy.",
    },
    {
      id: "cctv",
      title: "CCTV Operators",
      image: cctvOperators,
      slots: 8,
      location: "Nationwide",
      status: "hiring",
      description:
        "Monitor security cameras and surveillance systems. Report suspicious activities and maintain security logs. Basic computer skills required.",
    },
    {
      id: "concierge",
      title: "Concierge Services",
      image: conciergeServices,
      slots: 4,
      location: "Metro Manila",
      status: "hiring",
      description:
        "Provide front desk assistance, guest services, and lobby management. Excellent customer service and professional appearance required.",
    },
    {
      id: "tempclerk",
      title: "Office Temp Clerks",
      image: officeTempClerks,
      slots: 10,
      location: "Nationwide",
      status: "hiring",
      description:
        "Perform general clerical duties including filing, data entry, and document organization. Basic computer literacy and attention to detail needed.",
    },
    {
      id: "promo",
      title: "Promotion Merchandisers",
      image: promotionMerchandisers,
      slots: 12,
      location: "Nationwide",
      status: "hiring",
      description:
        "Promote products in retail stores and supermarkets. Arrange merchandise displays and engage customers. Outgoing personality preferred.",
    },
    {
      id: "radio",
      title: "Radio Operators",
      image: radioOperators,
      slots: 3,
      location: "Davao, Cebu",
      status: "limited",
      description:
        "Operate radio communication equipment for coordination and dispatch. Must be able to work in shifts and have clear communication skills.",
    },
    {
      id: "other",
      title: "Other Allied Services",
      image: otherAlliedServices,
      slots: 20,
      location: "Nationwide",
      status: "hiring",
      description:
        "Various support roles including utility workers, helpers, and assistants. Flexible positions available across different industries.",
    },
  ];

  // Office Staff positions with images
  const officeStaffPositions = [
    {
      id: "hr",
      title: "HR Staff",
      image: hrStaff,
      slots: 2,
      location: "Davao City",
      status: "hiring",
      description:
        "Handle recruitment, employee relations, and HR documentation. Assist in onboarding and personnel file management. HR background preferred.",
    },
    {
      id: "accounting",
      title: "Accounting Staff",
      image: accountingStaff,
      slots: 3,
      location: "Davao City",
      status: "hiring",
      description:
        "Process financial transactions, maintain accounting records, and assist in financial reporting. Accounting degree or experience required.",
    },
    {
      id: "payroll",
      title: "Payroll & Billing Staff",
      image: payrollBillingStaff,
      slots: 2,
      location: "Davao City",
      status: "hiring",
      description:
        "Compute employee salaries, process payroll, and handle billing operations. Must be proficient in Excel and accounting software.",
    },
    {
      id: "areahead",
      title: "Area Head",
      image: areaHead,
      slots: 1,
      location: "Davao, Cebu, Manila",
      status: "limited",
      description:
        "Supervise field operations in assigned area. Manage personnel deployment and client relations. Leadership experience required.",
    },
    {
      id: "201member",
      title: "201 File Member",
      image: member201,
      slots: 2,
      location: "Davao City",
      status: "hiring",
      description:
        "Maintain and organize employee 201 files. Handle document processing and records management. Must be organized and detail-oriented.",
    },
  ];

  // Auto-advance for Field Service carousel (pause on hover)
  useEffect(() => {
    if (isFieldCarouselPaused) return;

    const checkInactivity = setInterval(() => {
      const timeSinceLastInteraction =
        Date.now() - lastFieldInteractionRef.current;
      if (timeSinceLastInteraction >= 4000) {
        setActiveFieldSlide(
          (prev) => (prev + 1) % fieldServicePositions.length,
        );
        lastFieldInteractionRef.current = Date.now();
      }
    }, 1000);
    return () => clearInterval(checkInactivity);
  }, [fieldServicePositions.length, isFieldCarouselPaused]);

  // Auto-advance for Office Staff carousel (pause on hover)
  useEffect(() => {
    if (isOfficeCarouselPaused) return;

    const checkInactivity = setInterval(() => {
      const timeSinceLastInteraction =
        Date.now() - lastOfficeInteractionRef.current;
      if (timeSinceLastInteraction >= 4000) {
        setActiveOfficeSlide(
          (prev) => (prev + 1) % officeStaffPositions.length,
        );
        lastOfficeInteractionRef.current = Date.now();
      }
    }, 1000);
    return () => clearInterval(checkInactivity);
  }, [officeStaffPositions.length, isOfficeCarouselPaused]);

  // Carousel navigation functions
  const goToNextFieldSlide = () => {
    setActiveFieldSlide((prev) => (prev + 1) % fieldServicePositions.length);
    lastFieldInteractionRef.current = Date.now();
  };

  const goToPrevFieldSlide = () => {
    setActiveFieldSlide(
      (prev) =>
        (prev - 1 + fieldServicePositions.length) %
        fieldServicePositions.length,
    );
    lastFieldInteractionRef.current = Date.now();
  };

  const goToNextOfficeSlide = () => {
    setActiveOfficeSlide((prev) => (prev + 1) % officeStaffPositions.length);
    lastOfficeInteractionRef.current = Date.now();
  };

  const goToPrevOfficeSlide = () => {
    setActiveOfficeSlide(
      (prev) =>
        (prev - 1 + officeStaffPositions.length) % officeStaffPositions.length,
    );
    lastOfficeInteractionRef.current = Date.now();
  };

  // Field Worker Application Form - Basic Employee Information (Simplified)
  const [fieldWorkerForm, setFieldWorkerForm] = useState({
    // Personal Information
    fullName: "",
    age: "",
    sex: "",
    civilStatus: "",
    birthDate: "",
    birthPlace: "",
    addressLine1: "",
    barangay: "",
    cityMunicipality: "",
    province: "",
    postalCode: "",
    contactNumber: "",
    emailAddress: "",
    // Job Preferences
    preferredPosition: "",
    otherService: "", // For "Other Allied Services" option
    preferredLocation: "",
    // Agreements
    agreeWarranty: false,
    agreeDataPrivacy: false,
  });

  // Form validation errors
  const [formErrors, setFormErrors] = useState({});

  // Office Staff Application Form - Personal Information Only
  const [officeStaffForm, setOfficeStaffForm] = useState({
    fullName: "",
    age: "",
    sex: "",
    civilStatus: "",
    birthDate: "",
    birthPlace: "",
    addressLine1: "",
    barangay: "",
    cityMunicipality: "",
    province: "",
    postalCode: "",
    contactNumber: "",
    emailAddress: "",
    // Position Applied (auto-filled from selected position)
    positionApplied: "",
    // Agreements
    agreeWarranty: false,
    agreeDataPrivacy: false,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    // Small delay to ensure DOM is updated after state change
    const timeoutId = setTimeout(() => {
      const elements = document.querySelectorAll('[class*="scroll-"]');
      elements.forEach((el) => observer.observe(el));
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [selectedPath, submissionStatus]);

  const fieldWorkerPositions = [
    "Janitorial Staff",
    "Messenger/Collector",
    "Company Driver",
    "Construction Worker",
    "Promotion Merchandiser",
    "CCTV Operator",
    "Radio Operator",
    "Carpet Shampooing Staff",
    "Office Temp Clerk",
    "Cash Sorter",
    "Concierge",
    "Other Allied Services",
  ];

  const officePositions = [
    "Administrative Assistant",
    "HR Staff",
    "Accounting Staff",
    "Billing Staff",
    "Operations Coordinator",
    "Branch Manager",
    "IT Support",
    "Marketing Staff",
    "Customer Service Representative",
    "Other",
  ];

  const cityProvinceOptions = [
    { city: "Caloocan City", province: "Metro Manila" },
    { city: "Makati City", province: "Metro Manila" },
    { city: "Manila City", province: "Metro Manila" },
    { city: "Pasig City", province: "Metro Manila" },
    { city: "Quezon City", province: "Metro Manila" },
    { city: "Taguig City", province: "Metro Manila" },
    { city: "Bacolod City", province: "Negros Occidental" },
    { city: "Cebu City", province: "Cebu" },
    { city: "Davao City", province: "Davao del Sur" },
    { city: "General Santos City", province: "South Cotabato" },
    { city: "Iloilo City", province: "Iloilo" },
    { city: "Cagayan de Oro City", province: "Misamis Oriental" },
    { city: "Zamboanga City", province: "Zamboanga del Sur" },
  ];

  const OTHER_CITY_OPTION = "Other / Not Listed";
  const OTHER_PROVINCE_OPTION = "Other / Not Listed";

  const allProvinceOptions = Array.from(
    new Set(cityProvinceOptions.map((option) => option.province)),
  ).sort((a, b) => a.localeCompare(b));

  const cityOptions = [
    ...Array.from(
      new Set(cityProvinceOptions.map((option) => option.city)),
    ).sort((a, b) => a.localeCompare(b)),
    OTHER_CITY_OPTION,
  ];

  const getProvinceOptionsForCity = (selectedCity) => {
    if (!selectedCity) return [];

    if (selectedCity === OTHER_CITY_OPTION) {
      return [...allProvinceOptions, OTHER_PROVINCE_OPTION];
    }

    const matchingProvinces = Array.from(
      new Set(
        cityProvinceOptions
          .filter((option) => option.city === selectedCity)
          .map((option) => option.province),
      ),
    );

    return matchingProvinces.length
      ? matchingProvinces
      : [...allProvinceOptions, OTHER_PROVINCE_OPTION];
  };

  const fieldWorkerProvinceOptions = getProvinceOptionsForCity(
    fieldWorkerForm.cityMunicipality,
  );
  const officeStaffProvinceOptions = getProvinceOptionsForCity(
    officeStaffForm.cityMunicipality,
  );

  const requirements = [
    {
      number: 1,
      title: "Barangay Clearance",
      description: "From your current residence",
    },
    {
      number: 2,
      title: "Fiscal Clearance",
      description: "Certificate from the local fiscal office",
    },
    {
      number: 3,
      title: "Police Clearance",
      description: "Recent clearance from local police",
    },
    {
      number: 4,
      title: "NBI Clearance",
      description: "National Bureau of Investigation clearance",
    },
    {
      number: 5,
      title: "Drug Test",
      description: "Certificate from accredited clinic",
    },
    {
      number: 6,
      title: "Psycho-Neuro Exam",
      description: "Certificate from accredited clinic",
    },
    {
      number: 7,
      title: "Physical Check-up",
      description: "Medical certificate from accredited clinic",
    },
    {
      number: 8,
      title: "SSS/PhilHealth/Pag-IBIG",
      description: "E1 or ID from government agencies",
    },
  ];

  const benefits = [
    {
      icon: <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>₱</span>,
      title: "Updated SSS Remittances",
      description: "We ensure your SSS contributions are always up to date.",
    },
    {
      icon: <FiShield />,
      title: "PHIC (PhilHealth)",
      description: "Complete health insurance coverage for all personnel.",
    },
    {
      icon: <FiTrendingUp />,
      title: "Pag-IBIG Fund",
      description: "Housing fund contributions for your future.",
    },
    {
      icon: <FiAward />,
      title: "13th Month Pay",
      description: "Mandatory 13th month bonus every year.",
    },
    {
      icon: <FiHeart />,
      title: "5 Days Incentive Leave",
      description: "Five (5) days incentive leave with pay annually.",
    },
    {
      icon: <FiUsers />,
      title: "Pro-Employee Policy",
      description: "We give what is due to employees - always.",
    },
  ];

  const handleFieldWorkerChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // For contactNumber, only allow digits and max 11 characters
    if (name === "contactNumber") {
      newValue = value.replace(/\D/g, "").slice(0, 11);
    }

    setFieldWorkerForm((prev) => {
      const nextForm = {
        ...prev,
        [name]: newValue,
      };

      if (name === "cityMunicipality") {
        nextForm.province = "";
      }

      return nextForm;
    });

    if (
      formErrors[name] ||
      (name === "cityMunicipality" && formErrors.province)
    ) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        if (name === "cityMunicipality") {
          delete newErrors.province;
        }
        return newErrors;
      });
    }
  };

  const handleOfficeStaffChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === "checkbox" ? checked : value;

    // For contactNumber, only allow digits and max 11 characters
    if (name === "contactNumber") {
      newValue = value.replace(/\D/g, "").slice(0, 11);
    }

    setOfficeStaffForm((prev) => {
      const nextForm = {
        ...prev,
        [name]: newValue,
      };

      if (name === "cityMunicipality") {
        nextForm.province = "";
      }

      return nextForm;
    });

    // Clear error for this field when user starts typing/selecting
    if (
      formErrors[name] ||
      (name === "cityMunicipality" && formErrors.province)
    ) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        if (name === "cityMunicipality") {
          delete newErrors.province;
        }
        return newErrors;
      });
    }

    // Real-time validation for specific fields
    if (name === "contactNumber") {
      const contactDigits = newValue;
      if (contactDigits.length === 11) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.contactNumber;
          return newErrors;
        });
      }
    }

    if (name === "age") {
      const ageNum = parseInt(value, 10);
      if (ageNum >= 18) {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors.age;
          return newErrors;
        });
      }
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        setFormErrors((prev) => ({
          ...prev,
          resume: "Please upload a PDF or Word document (.pdf, .doc, .docx)",
        }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors((prev) => ({
          ...prev,
          resume: "File size must be less than 5MB",
        }));
        return;
      }
      setResumeFile(file);
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.resume;
        return newErrors;
      });
    }
  };

  const openSelectedFile = (file) => {
    if (!file) return;
    const fileUrl = URL.createObjectURL(file);
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
  };

  const openResumeFilePicker = () => {
    if (resumeInputRef.current) {
      resumeInputRef.current.value = "";
      resumeInputRef.current.click();
    }
  };

  const clearResumeFile = () => {
    setResumeFile(null);
    if (resumeInputRef.current) {
      resumeInputRef.current.value = "";
    }
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.resume;
      return newErrors;
    });
  };

  const composeAddress = (addressLine1, barangay, city, province, postalCode) =>
    [addressLine1, barangay, city, province, postalCode].filter(Boolean).join(", ");

  const handleTypingTestProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setFormErrors((prev) => ({
          ...prev,
          typingTestProof:
            "Please upload an image file (.png, .jpg, .jpeg, .webp)",
        }));
        setTypingTestProofFile(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors((prev) => ({
          ...prev,
          typingTestProof: "File size must be less than 5MB",
        }));
        setTypingTestProofFile(null);
        return;
      }
      setTypingTestProofFile(file);
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.typingTestProof;
        return newErrors;
      });
    }
  };

  const handlePersonalityTestProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        setFormErrors((prev) => ({
          ...prev,
          personalityTestProof:
            "Please upload an image file (.png, .jpg, .jpeg, .webp)",
        }));
        setPersonalityTestProofFile(null);
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setFormErrors((prev) => ({
          ...prev,
          personalityTestProof: "File size must be less than 5MB",
        }));
        setPersonalityTestProofFile(null);
        return;
      }
      setPersonalityTestProofFile(file);
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.personalityTestProof;
        return newErrors;
      });
    }
  };

  // Typing Test Functions
  const startTypingTest = () => {
    const randomPassage =
      typingTestPassages[Math.floor(Math.random() * typingTestPassages.length)];
    setTypingTestText(randomPassage);
    setTypingUserInput("");
    typingUserInputRef.current = "";
    setTypingTimeLeft(60);
    setTypingResults(null);
    setAnimatedResults({ grossWpm: 0, accuracy: 0, netWpm: 0 });
    setShowResultsAnimation(false);
    setTypingTimerStarted(false);
    setTypingTestStatus("ready"); // Ready state - waiting for user to start typing

    // Focus the input
    setTimeout(() => {
      if (typingInputRef.current) {
        typingInputRef.current.focus();
      }
    }, 100);
  };

  const beginTimer = () => {
    if (!typingTimerStarted) {
      setTypingTimerStarted(true);
      setTypingStartTime(Date.now());
      setTypingTestStatus("running");
    }
  };

  const calculateTypingResults = (currentInput, timeRemaining) => {
    const timeElapsed = 60 - timeRemaining; // seconds used
    const timeInMinutes = timeElapsed / 60;

    if (timeInMinutes <= 0 || currentInput.length === 0) {
      return {
        grossWpm: 0,
        accuracy: 0,
        netWpm: 0,
        correctChars: 0,
        totalChars: 0,
        errors: 0,
      };
    }

    // Count correct and incorrect characters
    const inputChars = currentInput.split("");
    const textChars = typingTestText.split("");
    let correctChars = 0;
    let incorrectChars = 0;

    inputChars.forEach((char, index) => {
      if (index < textChars.length) {
        if (char === textChars[index]) {
          correctChars++;
        } else {
          incorrectChars++;
        }
      } else {
        // Extra characters typed beyond the original text
        incorrectChars++;
      }
    });

    // Calculate WPM (5 characters = 1 word)
    const grossWpm = Math.round(currentInput.length / 5 / timeInMinutes);

    // Calculate accuracy based on correct characters
    const accuracy =
      currentInput.length > 0
        ? Math.round((correctChars / currentInput.length) * 100)
        : 0;

    // Calculate net WPM (gross WPM × accuracy percentage)
    const netWpm = Math.round(grossWpm * (accuracy / 100));

    return {
      grossWpm,
      accuracy,
      netWpm,
      correctChars,
      totalChars: currentInput.length,
      errors: incorrectChars, // Now errors = incorrect characters, matching accuracy
    };
  };

  const getSpeedCategory = (netWpm) => {
    if (netWpm < 25) return { label: "Slow", color: "#ef4444" };
    if (netWpm < 45) return { label: "Average", color: "#f59e0b" };
    if (netWpm < 65) return { label: "Fluent", color: "#10b981" };
    if (netWpm < 85) return { label: "Fast", color: "#3b82f6" };
    return { label: "Pro", color: "#8b5cf6" };
  };

  const animateResults = (finalResults) => {
    setShowResultsAnimation(true);
    const duration = 1500; // 1.5 seconds animation
    const steps = 30;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      // Easing function for smooth animation
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setAnimatedResults({
        grossWpm: Math.round(finalResults.grossWpm * easeOut),
        accuracy: Math.round(finalResults.accuracy * easeOut),
        netWpm: Math.round(finalResults.netWpm * easeOut),
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setAnimatedResults(finalResults);
      }
    }, stepDuration);
  };

  const finishTypingTest = (timeRemaining) => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    // Use the ref value which has the latest input
    const currentInput = typingUserInputRef.current;
    const results = calculateTypingResults(currentInput, timeRemaining);
    setTypingResults(results);
    setTypingTestStatus("completed");

    // Start the animation
    setTimeout(() => {
      animateResults(results);
    }, 300);
  };

  const retakeTypingTest = () => {
    setTypingTestStatus("ready"); // Set to 'ready' so user can type immediately
    setTypingResults(null);
    setTypingUserInput("");
    typingUserInputRef.current = "";
    setTypingTimeLeft(60);
    setAnimatedResults({ grossWpm: 0, accuracy: 0, netWpm: 0 });
    setShowResultsAnimation(false);
    setTypingTimerStarted(false);
  };

  const handleTypingInput = (e) => {
    if (typingTestStatus !== "ready" && typingTestStatus !== "running") return;

    const newValue = e.target.value;

    // Start the timer when user begins typing
    if (typingTestStatus === "ready" && newValue.length > 0) {
      beginTimer();
    }

    setTypingUserInput(newValue);
    typingUserInputRef.current = newValue; // Keep ref in sync
  };

  // Timer effect for typing test
  useEffect(() => {
    if (typingTestStatus === "running" && typingTimeLeft > 0) {
      typingTimerRef.current = setInterval(() => {
        setTypingTimeLeft((prev) => {
          if (prev <= 1) {
            // Time's up - finish the test
            setTimeout(() => finishTypingTest(0), 0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (typingTimerRef.current) {
          clearInterval(typingTimerRef.current);
        }
      };
    }
  }, [typingTestStatus]);

  // Handle manual finish (Finish Early button)
  const handleFinishEarly = () => {
    finishTypingTest(typingTimeLeft);
  };

  // Personality Test (16PF) Functions
  const startPersonalityTest = () => {
    setPersonalityTestStatus("running");
    setPersonalityCurrentQuestion(0);
    setPersonalityAnswers({});
    setPersonalityResults(null);
    setShowPersonalityResultsAnimation(false);
  };

  const handlePersonalityAnswer = (questionId, answer) => {
    setPersonalityAnswers((prev) => ({
      ...prev,
      [questionId]: answer,
    }));

    // Auto-advance to next question after a short delay
    setTimeout(() => {
      if (personalityCurrentQuestion < personalityQuestions.length - 1) {
        setPersonalityCurrentQuestion((prev) => prev + 1);
      }
    }, 300);
  };

  const goToPreviousPersonalityQuestion = () => {
    if (personalityCurrentQuestion > 0) {
      setPersonalityCurrentQuestion((prev) => prev - 1);
    }
  };

  const goToNextPersonalityQuestion = () => {
    if (personalityCurrentQuestion < personalityQuestions.length - 1) {
      setPersonalityCurrentQuestion((prev) => prev + 1);
    }
  };

  const calculatePersonalityResults = () => {
    // Initialize factor scores for all 16 factors
    const factorScores = {};
    const factorCounts = {};

    // Initialize all factors
    Object.keys(personalityFactors).forEach((factor) => {
      factorScores[factor] = 0;
      factorCounts[factor] = 0;
    });

    // Calculate scores for each factor
    // Scale: 1 (Strongly Disagree) to 5 (Strongly Agree)
    personalityQuestions.forEach((question) => {
      const answer = personalityAnswers[question.id];
      if (answer !== undefined) {
        const factor = question.factor;
        const keyed = question.keyed;

        // If keyed '+', higher answer = higher factor score
        // If keyed '-', higher answer = lower factor score (reverse scored)
        if (keyed === "+") {
          factorScores[factor] += answer;
        } else {
          factorScores[factor] += 6 - answer; // Reverse scoring
        }
        factorCounts[factor] += 1;
      }
    });

    // Calculate percentages (1-10 scale like original 16PF)
    const factorResults = {};
    Object.keys(personalityFactors).forEach((factor) => {
      const count = factorCounts[factor];
      if (count > 0) {
        // Calculate average (1-5), then convert to 1-10 scale
        const average = factorScores[factor] / count;
        // Convert 1-5 average to 1-10 sten score
        const stenScore = Math.round(((average - 1) / 4) * 9 + 1);
        // Also calculate percentage for display
        const percentage = Math.round(((average - 1) / 4) * 100);

        factorResults[factor] = {
          score: factorScores[factor],
          count: count,
          average: average,
          stenScore: Math.min(10, Math.max(1, stenScore)),
          percentage: percentage,
          isHigh: percentage >= 50,
          factorInfo: personalityFactors[factor],
        };
      } else {
        factorResults[factor] = {
          score: 0,
          count: 0,
          average: 3,
          stenScore: 5,
          percentage: 50,
          isHigh: false,
          factorInfo: personalityFactors[factor],
        };
      }
    });

    // Find strongest and weakest traits
    const sortedFactors = Object.entries(factorResults).sort(
      (a, b) => b[1].percentage - a[1].percentage,
    );

    const strongestTraits = sortedFactors.slice(0, 3);
    const weakestTraits = sortedFactors.slice(-3).reverse();

    return {
      factors: factorResults,
      strongestTraits,
      weakestTraits,
      totalQuestions: personalityQuestions.length,
      answeredQuestions: Object.keys(personalityAnswers).length,
    };
  };

  const finishPersonalityTest = () => {
    // Check if all questions are answered
    const firstUnansweredIndex = personalityQuestions.findIndex(
      (q) => personalityAnswers[q.id] === undefined,
    );

    if (firstUnansweredIndex !== -1) {
      // Navigate to the first unanswered question
      setPersonalityCurrentQuestion(firstUnansweredIndex);
      // Scroll to top of the test section
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // All questions answered - proceed with results
    const results = calculatePersonalityResults();
    setPersonalityResults(results);
    setPersonalityTestStatus("completed");

    // Start the animation
    setTimeout(() => {
      setShowPersonalityResultsAnimation(true);
    }, 300);
  };

  const retakePersonalityTest = () => {
    setPersonalityTestStatus("idle");
    setPersonalityCurrentQuestion(0);
    setPersonalityAnswers({});
    setPersonalityResults(null);
    setShowPersonalityResultsAnimation(false);
  };

  const getAnsweredQuestionsCount = () => {
    return Object.keys(personalityAnswers).length;
  };

  const handleIqTestAnswer = (questionId, answerIndex) => {
    setIqTestAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex,
    }));
  };

  const handleAbstractTestAnswer = (questionId, answerIndex) => {
    setAbstractTestAnswers((prev) => ({
      ...prev,
      [questionId]: answerIndex,
    }));
  };

  const calculateIqScore = () => {
    let correct = 0;
    iqTestAttemptQuestions.forEach((q) => {
      if (iqTestAnswers[q.id] === q.correct) {
        correct++;
      }
    });
    return {
      correct,
      total: iqTestAttemptQuestions.length,
      percentage: Math.round((correct / iqTestAttemptQuestions.length) * 100),
    };
  };

  const calculateAbstractScore = () => {
    let correct = 0;
    abstractAttemptQuestions.forEach((q) => {
      if (abstractTestAnswers[q.id] === q.correct) {
        correct++;
      }
    });
    return {
      correct,
      total: abstractAttemptQuestions.length,
      percentage: Math.round((correct / abstractAttemptQuestions.length) * 100),
    };
  };

  // Math Test Functions
  const startMathTest = () => {
    setMathTestStatus("running");
    setMathTestTimeLeft(15 * 60); // 15 minutes

    // Start the timer
    mathTestTimerRef.current = setInterval(() => {
      setMathTestTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(mathTestTimerRef.current);
          finishMathTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleMathTestAnswer = (questionId, field, value) => {
    // Only allow numbers
    const numValue = value.replace(/[^0-9]/g, "");
    setMathTestAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: numValue,
      },
    }));
  };

  const calculateMathScore = () => {
    let correct = 0;
    mathTimeQuestions.forEach((q) => {
      const answer = mathTestAnswers[q.id];
      if (answer) {
        const userHours = parseInt(answer.hours) || 0;
        const userMinutes = parseInt(answer.minutes) || 0;
        const userTotalMinutes = parseInt(answer.totalMinutes) || 0;

        // Check if hours and minutes are correct (allow either format)
        const hoursMinutesCorrect =
          userHours === q.correctHours && userMinutes === q.correctMinutes;
        const totalMinutesCorrect = userTotalMinutes === q.totalMinutes;

        if (hoursMinutesCorrect && totalMinutesCorrect) {
          correct++;
        }
      }
    });
    return {
      correct,
      total: mathTimeQuestions.length,
      percentage: Math.round((correct / mathTimeQuestions.length) * 100),
    };
  };

  const finishMathTest = () => {
    if (mathTestTimerRef.current) {
      clearInterval(mathTestTimerRef.current);
    }
    const results = calculateMathScore();
    setMathTestResults(results);
    setMathTestStatus("completed");
  };

  const formatMathTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Cleanup math test timer on unmount
  useEffect(() => {
    return () => {
      if (mathTestTimerRef.current) {
        clearInterval(mathTestTimerRef.current);
      }
      if (essayTimerRef.current) {
        clearInterval(essayTimerRef.current);
      }
    };
  }, []);

  // Essay Writing Test Functions
  const startEssayTest = () => {
    setEssayTestStatus("running");
    setEssayTimeLeft(20 * 60); // 20 minutes
    setEssayContent("");
    setEssayWordCount(0);
    setEssayTimedOut(false);

    // Start the timer
    essayTimerRef.current = setInterval(() => {
      setEssayTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(essayTimerRef.current);
          // Auto-submit on timeout
          handleEssayTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleEssayChange = (e) => {
    const content = e.target.value;
    setEssayContent(content);

    // Count words (split by whitespace and filter empty strings)
    const words = content
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setEssayWordCount(words.length);
  };

  // Handle essay timeout - auto submit without retry option
  const handleEssayTimeout = () => {
    if (essayTimerRef.current) {
      clearInterval(essayTimerRef.current);
    }
    setEssayTimedOut(true);
    setEssayTestStatus("completed");
    setShowEssayTimeoutModal(true);
  };

  const finishEssayTest = () => {
    // Check if word count is below minimum - show warning but allow to proceed
    if (essayWordCount < 200) {
      setShowWordCountWarning(true);
      return;
    }

    if (essayTimerRef.current) {
      clearInterval(essayTimerRef.current);
    }
    setEssayTestStatus("completed");
  };

  // Confirm finish even with low word count
  const confirmFinishEssay = () => {
    setShowWordCountWarning(false);
    if (essayTimerRef.current) {
      clearInterval(essayTimerRef.current);
    }
    setEssayTestStatus("completed");
  };

  const formatEssayTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Excel Proficiency Test Functions
  const startExcelTest = () => {
    setExcelTestStatus("running");
    setExcelTimeLeft(15 * 60); // 15 minutes
    setExcelFile(null);

    // Start the timer
    excelTimerRef.current = setInterval(() => {
      setExcelTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(excelTimerRef.current);
          finishExcelTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Accept Excel files
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.oasis.opendocument.spreadsheet",
      ];
      const validExtensions = [".xls", ".xlsx", ".ods"];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (
        validTypes.includes(file.type) ||
        validExtensions.includes(fileExtension)
      ) {
        setExcelFile(file);
      } else {
        alert("Please upload a valid Excel file (.xls, .xlsx, or .ods)");
        e.target.value = "";
      }
    }
  };

  const finishExcelTest = () => {
    if (excelTimerRef.current) {
      clearInterval(excelTimerRef.current);
    }
    setExcelTestStatus("completed");
  };

  const formatExcelTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleFieldWorkerSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const errors = validateFieldWorkerForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const fieldWorkerPayload = {
        ...fieldWorkerForm,
        address: composeAddress(
          fieldWorkerForm.addressLine1,
          fieldWorkerForm.barangay,
          fieldWorkerForm.cityMunicipality,
          fieldWorkerForm.province,
          fieldWorkerForm.postalCode,
        ),
      };
      const result = await submitFieldWorkerApplication(fieldWorkerPayload);
      setApplicationId(result.applicationId);
      setSubmissionStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
      console.log(
        "Field Worker Application submitted successfully:",
        result.applicationId,
      );
    } catch (error) {
      console.error("Error submitting application:", error);
      setErrorMessage(
        error.message || "Failed to submit application. Please try again.",
      );
      setSubmissionStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOfficeStaffSubmit = async (e) => {
    e.preventDefault();

    // Validate IQ test - all questions must be answered
    const unanswered = iqTestAttemptQuestions.filter(
      (q) => iqTestAnswers[q.id] === undefined,
    );
    if (unanswered.length > 0) {
      setFormErrors({
        iqTest: `Please answer all questions. ${unanswered.length} question(s) remaining.`,
      });
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    const unansweredAbstract = abstractAttemptQuestions.filter(
      (q) => abstractTestAnswers[q.id] === undefined,
    );
    if (unansweredAbstract.length > 0) {
      setFormErrors({
        abstractTest: `Please answer all questions. ${unansweredAbstract.length} question(s) remaining.`,
      });
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    // Validate math test completed
    if (mathTestStatus !== "completed" || !mathTestResults) {
      setFormErrors({
        mathTest: "Please complete the math test before submitting.",
      });
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    // Validate essay test completed (allow any word count)
    if (essayTestStatus !== "completed") {
      setFormErrors({ essayTest: "Please complete the essay writing test." });
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    // Validate Excel test completed
    if (excelTestStatus !== "completed" || !excelFile) {
      setFormErrors({
        excelTest:
          "Please complete the Excel proficiency test and upload your file.",
      });
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const iqScore = calculateIqScore();
      const abstractScore = calculateAbstractScore();
      const fullOfficeAddress = composeAddress(
        officeStaffForm.addressLine1,
        officeStaffForm.barangay,
        officeStaffForm.cityMunicipality,
        officeStaffForm.province,
        officeStaffForm.postalCode,
      );
      const formDataWithScore = {
        ...officeStaffForm,
        address: fullOfficeAddress,
        resumeFileName: resumeFile ? resumeFile.name : "No resume attached",
        resumeFile: resumeFile || null,
        // Test Scores (summary strings)
        iqTestScore: `${iqScore.correct}/${iqScore.total} (${iqScore.percentage}%)`,
        abstractReasoningScore: `${abstractScore.correct}/${abstractScore.total} (${abstractScore.percentage}%)`,
        mathTestScore: `${mathTestResults.correct}/${mathTestResults.total} (${mathTestResults.percentage}%)`,
        typingTestResults: typingResults
          ? `${typingResults.netWpm} WPM (${typingResults.accuracy}% accuracy)`
          : "Not completed",
        personalityTestResults: personalityResults
          ? "Completed"
          : "Not completed",
        essayContent: essayContent,
        essayWordCount: essayWordCount,
        excelFileName: excelFile ? excelFile.name : "No file uploaded",
        excelFile: excelFile || null,
        // Detailed test data for admin panel
        iqTestDetails: {
          score: iqScore,
          answers: { ...iqTestAnswers },
          questions: iqTestAttemptQuestions.map(q => ({ id: q.id, text: q.text, options: q.options, correct: q.correct, userAnswer: iqTestAnswers[q.id] })),
        },
        abstractTestDetails: {
          score: abstractScore,
          answers: { ...abstractTestAnswers },
          questions: abstractAttemptQuestions.map(q => ({ id: q.id, options: q.options, correct: q.correct, userAnswer: abstractTestAnswers[q.id] })),
        },
        typingTestDetails: typingResults ? {
          netWpm: typingResults.netWpm,
          grossWpm: typingResults.grossWpm,
          accuracy: typingResults.accuracy,
          totalChars: typingResults.totalChars,
          errors: typingResults.errors,
        } : null,
        mathTestDetails: mathTestResults ? {
          correct: mathTestResults.correct,
          total: mathTestResults.total,
          percentage: mathTestResults.percentage,
          answers: mathTestResults.answers || null,
        } : null,
        personalityTestDetails: personalityResults || null,
      };
      const result = await submitOfficeStaffApplication(formDataWithScore);
      setApplicationId(result.applicationId);
      setSubmissionStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
      console.log(
        "Office Staff Application submitted successfully:",
        result.applicationId,
      );
    } catch (error) {
      console.error("Error submitting application:", error);
      setErrorMessage(
        error.message || "Failed to submit application. Please try again.",
      );
      setSubmissionStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToCareerOptions = () => {
    setSelectedPath(null);
    setSelectedPosition(null);
    setFormStep(1);
    setSubmissionStatus(null);
    setApplicationId(null);
    setErrorMessage(null);
    setFormErrors({});

    // Clear field worker form
    setFieldWorkerForm({
      fullName: "",
      age: "",
      sex: "",
      civilStatus: "",
      birthDate: "",
      birthPlace: "",
      addressLine1: "",
      barangay: "",
      cityMunicipality: "",
      province: "",
      postalCode: "",
      contactNumber: "",
      emailAddress: "",
      preferredPosition: "",
      otherService: "",
      preferredLocation: "",
      agreeWarranty: false,
      agreeDataPrivacy: false,
    });

    // Clear office staff form
    setOfficeStaffForm({
      fullName: "",
      age: "",
      sex: "",
      civilStatus: "",
      birthDate: "",
      birthPlace: "",
      addressLine1: "",
      barangay: "",
      cityMunicipality: "",
      province: "",
      postalCode: "",
      contactNumber: "",
      emailAddress: "",
      positionApplied: "",
      agreeWarranty: false,
      agreeDataPrivacy: false,
    });

    // Clear file uploads
    setResumeFile(null);

    // Reset Excel test state
    setExcelTestStatus("idle");
    setExcelTimeLeft(15 * 60);
    setExcelFile(null);
    if (excelTimerRef.current) {
      clearInterval(excelTimerRef.current);
    }

    // Scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setFormErrors({});
    if (formStep > 1) {
      setFormStep(formStep - 1);
      // Scroll to top when going back
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      // Going back from form to hiring sections
      setSelectedPath(null);
      setSelectedPosition(null);
    }
  };

  const handleSelectPosition = (position, pathType) => {
    setSelectedPosition(position);
    setSelectedPath(pathType);
    setFormErrors({});
    // Pre-fill the preferred position in the form
    if (pathType === "fieldWorker") {
      setFieldWorkerForm((prev) => ({
        ...prev,
        preferredPosition: position.title,
      }));
    } else {
      // Reset office staff form with the selected position pre-filled
      setOfficeStaffForm({
        fullName: "",
        age: "",
        sex: "",
        civilStatus: "",
        birthDate: "",
        birthPlace: "",
        addressLine1: "",
        barangay: "",
        cityMunicipality: "",
        province: "",
        postalCode: "",
        contactNumber: "",
        emailAddress: "",
        positionApplied: position.title,
        agreeWarranty: false,
        agreeDataPrivacy: false,
      });
      setResumeFile(null);
      setTypingTestProofFile(null);
      setPersonalityTestProofFile(null);
      setIqTestAnswers({});
      setAbstractTestAnswers({});
      setIqTestAttemptQuestions(shuffleQuestions(iqTestQuestions));
      setAbstractAttemptQuestions(shuffleQuestions(abstractReasoningQuestions));
    }
  };

  // Validation for Field Worker Form (Simplified - single step)
  const validateFieldWorkerForm = () => {
    const errors = {};
    const form = fieldWorkerForm;

    // Personal Information validation
    if (!form.fullName.trim()) errors.fullName = "Full name is required";
    if (!form.age || form.age < 18) errors.age = "Valid age (18+) is required";
    if (!form.sex) errors.sex = "Sex is required";
    if (!form.civilStatus) errors.civilStatus = "Civil status is required";
    if (!form.birthDate) errors.birthDate = "Birthdate is required";
    if (!form.birthPlace.trim()) errors.birthPlace = "Birth place is required";
    if (!form.addressLine1.trim()) errors.addressLine1 = "Address Line 1 is required";
    if (!form.barangay.trim()) errors.barangay = "Barangay is required";
    if (!form.cityMunicipality.trim())
      errors.cityMunicipality = "City or municipality is required";
    if (!form.province.trim()) errors.province = "Province is required";
    if (!form.postalCode.trim()) errors.postalCode = "Postal code is required";

    // Contact number validation - must be exactly 11 digits
    if (!form.contactNumber.trim()) {
      errors.contactNumber = "Contact number is required";
    } else if (form.contactNumber.length !== 11) {
      errors.contactNumber = "Contact number must be exactly 11 digits";
    }

    if (!form.emailAddress.trim())
      errors.emailAddress = "Email address is required";

    // Job preferences
    if (!form.preferredPosition)
      errors.preferredPosition = "Preferred position is required";
    if (
      form.preferredPosition === "Other Allied Services" &&
      !form.otherService.trim()
    ) {
      errors.otherService = "Please specify the service you are applying for";
    }
    if (!form.preferredLocation)
      errors.preferredLocation = "Preferred location is required";

    return errors;
  };

  // Validation for Office Staff Form
  const validateOfficeStaffForm = () => {
    const errors = {};
    const form = officeStaffForm;

    if (!form.fullName.trim()) errors.fullName = "Full name is required";
    if (!form.age || form.age < 18) errors.age = "Valid age (18+) is required";
    if (!form.sex) errors.sex = "Sex is required";
    if (!form.civilStatus) errors.civilStatus = "Civil status is required";
    if (!form.birthDate) errors.birthDate = "Birthdate is required";
    if (!form.birthPlace.trim()) errors.birthPlace = "Birth place is required";
    if (!form.addressLine1.trim()) errors.addressLine1 = "Address Line 1 is required";
    if (!form.barangay.trim()) errors.barangay = "Barangay is required";
    if (!form.cityMunicipality.trim())
      errors.cityMunicipality = "City or municipality is required";
    if (!form.province.trim()) errors.province = "Province is required";
    if (!form.postalCode.trim()) errors.postalCode = "Postal code is required";

    // Contact number validation - must be exactly 11 digits
    if (!form.contactNumber.trim()) {
      errors.contactNumber = "Contact number is required";
    } else if (form.contactNumber.length !== 11) {
      errors.contactNumber = "Contact number must be exactly 11 digits";
    }

    if (!form.emailAddress.trim())
      errors.emailAddress = "Email address is required";
    if (!resumeFile) errors.resume = "Please attach your resume";

    return errors;
  };

  const nextStep = () => {
    let errors = {};

    if (selectedPath === "fieldWorker") {
      errors = validateFieldWorkerStep(formStep);
    } else if (selectedPath === "officeStaff") {
      if (formStep === 1) {
        errors = validateOfficeStaffForm();
      } else if (formStep === 2) {
        // Validate typing test completed (user must at least type something)
        if (typingTestStatus !== "completed" || !typingResults) {
          errors.typingTest =
            "Please complete the typing test before proceeding.";
        } else if (typingResults.totalChars === 0) {
          errors.typingTest =
            "Please type at least some characters to complete the test.";
        }
      } else if (formStep === 3) {
        // Validate built-in personality test completed
        if (personalityTestStatus !== "completed" || !personalityResults) {
          errors.personalityTest =
            "Please complete the personality test before proceeding.";
        }
      } else if (formStep === 4) {
        // Validate IQ test answers
        const unanswered = iqTestAttemptQuestions.filter(
          (q) => iqTestAnswers[q.id] === undefined,
        );
        if (unanswered.length > 0) {
          errors.iqTest = `Please answer all questions. ${unanswered.length} question(s) remaining.`;
        }
      } else if (formStep === 5) {
        // Validate Abstract test answers
        const unansweredAbstract = abstractAttemptQuestions.filter(
          (q) => abstractTestAnswers[q.id] === undefined,
        );
        if (unansweredAbstract.length > 0) {
          errors.abstractTest = `Please answer all questions. ${unansweredAbstract.length} question(s) remaining.`;
        }
      } else if (formStep === 6) {
        // Validate math test completed
        if (mathTestStatus !== "completed" || !mathTestResults) {
          errors.mathTest = "Please complete the math test before proceeding.";
        }
      } else if (formStep === 7) {
        // Validate essay test completed (allow any word count, just needs to be completed)
        if (essayTestStatus !== "completed") {
          errors.essayTest =
            "Please complete the essay writing test before proceeding.";
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      // Scroll to top of form to show errors
      window.scrollTo({ top: 300, behavior: "smooth" });
      return;
    }

    setFormErrors({});

    // Show Exam Introduction modal first, then Typing test modal when entering step 2 for office staff
    if (selectedPath === "officeStaff" && formStep === 1) {
      setShowExamIntroModal(true);
      return; // Don't advance yet, wait for user to acknowledge
    }

    // Show Personality test instructions modal when entering step 3 (Personality Test) for office staff
    if (selectedPath === "officeStaff" && formStep === 2) {
      setShowPersonalityTestModal(true);
    }

    // Show IQ test instructions modal when entering step 4 (IQ Test) for office staff
    if (selectedPath === "officeStaff" && formStep === 3) {
      setShowIqTestModal(true);
    }

    // Show Abstract test instructions modal when entering step 5 (Abstract Reasoning) for office staff
    if (selectedPath === "officeStaff" && formStep === 4) {
      setShowAbstractTestModal(true);
    }

    // Show Math test instructions modal when entering step 6 (Math Test) for office staff
    if (selectedPath === "officeStaff" && formStep === 5) {
      setShowMathTestModal(true);
    }

    // Show Essay test instructions modal when entering step 7 (Essay Writing) for office staff
    if (selectedPath === "officeStaff" && formStep === 6) {
      setShowEssayTestModal(true);
    }

    // Show Excel test instructions modal when entering step 8 (Excel Proficiency) for office staff
    if (selectedPath === "officeStaff" && formStep === 7) {
      setShowExcelTestModal(true);
    }

    setFormStep(formStep + 1);

    // Scroll to top of form when advancing to next step
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle proceeding from Exam Introduction to Typing Test
  const handleProceedToExams = () => {
    setShowExamIntroModal(false);
    setFormStep(2);
    setShowTypingTestModal(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Render Field Service Hirings Section
  // Render Combined Hiring Section (Field Service + Office Staff side by side)
  const renderHiringSection = () => (
    <section className="hiring-section combined-hiring-section section">
      <div className="container">
        <div className="hiring-columns">
          {/* Field Service Workers Column */}
          <div className="hiring-column field-service scroll-fade-left">
            <div
              className="hiring-slideshow-wrapper vertical"
              onMouseEnter={() => setIsFieldCarouselPaused(true)}
              onMouseLeave={() => setIsFieldCarouselPaused(false)}
            >
              <div className="hiring-slideshow">
                {fieldServicePositions.map((position, index) => (
                  <div
                    key={position.id}
                    className={`hiring-slide ${index === activeFieldSlide ? "active" : ""}`}
                  >
                    <div
                      className={`hiring-position-card vertical ${position.status}`}
                    >
                      <div className="position-image">
                        <img src={position.image} alt={position.title} />
                        <div className="position-status-badge">Now Hiring</div>
                        <div className="column-header">
                          <h3>Field Service Workers</h3>
                          <p>Apply for deployment to our partner companies</p>
                        </div>
                        <div className="position-content">
                          <div className="position-info">
                            <h3 className="position-title">{position.title}</h3>
                            <p className="position-description">
                              {position.description}
                            </p>
                          </div>
                          <button
                            className="btn btn-apply-position"
                            onClick={() =>
                              handleSelectPosition(position, "fieldWorker")
                            }
                          >
                            Apply Now <FiArrowRight />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation buttons */}
              <button
                className="slideshow-nav-btn prev"
                onClick={goToPrevFieldSlide}
              >
                <FiChevronLeft />
              </button>
              <button
                className="slideshow-nav-btn next"
                onClick={goToNextFieldSlide}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>

          {/* Office Staff Column */}
          <div className="hiring-column office-staff scroll-fade-right">
            <div
              className="hiring-slideshow-wrapper vertical office"
              onMouseEnter={() => setIsOfficeCarouselPaused(true)}
              onMouseLeave={() => setIsOfficeCarouselPaused(false)}
            >
              <div className="hiring-slideshow">
                {officeStaffPositions.map((position, index) => (
                  <div
                    key={position.id}
                    className={`hiring-slide ${index === activeOfficeSlide ? "active" : ""}`}
                  >
                    <div
                      className={`hiring-position-card vertical office ${position.status}`}
                    >
                      <div className="position-image">
                        <img src={position.image} alt={position.title} />
                        <div className="position-status-badge">Now Hiring</div>
                        <div className="column-header">
                          <h3>MEDCoop Office Staff</h3>
                          <p>Be part of our head office or branch team</p>
                        </div>
                        <div className="position-content">
                          <div className="position-info">
                            <h3 className="position-title">{position.title}</h3>
                            <p className="position-description">
                              {position.description}
                            </p>
                          </div>
                          <button
                            className="btn btn-apply-position office"
                            onClick={() =>
                              handleSelectPosition(position, "officeStaff")
                            }
                          >
                            Apply Now <FiArrowRight />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation buttons */}
              <button
                className="slideshow-nav-btn prev office"
                onClick={goToPrevOfficeSlide}
              >
                <FiChevronLeft />
              </button>
              <button
                className="slideshow-nav-btn next office"
                onClick={goToNextOfficeSlide}
              >
                <FiChevronRight />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  // Render Field Worker Form
  // Render Field Worker Form - Simplified single-page form (like Office Staff)
  const renderFieldWorkerForm = () => (
    <section className="application-form-section section">
      <div className="container">
        <div className="form-top-bar">
          <button className="back-btn" onClick={goBack}>
            <FiArrowLeft /> Back to Positions
          </button>
        </div>

        <div className="form-header">
          <div className="form-header-text">
            <h2>Field Service Worker Application</h2>
            <p>MEDCoop - Employee Information</p>
          </div>
        </div>

        <form
          className="hr-application-form"
          onSubmit={handleFieldWorkerSubmit}
        >
          <div className="form-step">
            <h3>
              <FiUser /> Personal Information
            </h3>
            {Object.keys(formErrors).length > 0 && (
              <div className="form-error-summary">
                <FiAlertCircle /> Please fill in all required fields before
                submitting.
              </div>
            )}

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={fieldWorkerForm.fullName}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. Juan A. Dela Cruz"
                  className={formErrors.fullName ? "error" : ""}
                />
                {formErrors.fullName && (
                  <span className="error-message">{formErrors.fullName}</span>
                )}
              </div>
              <div className="form-group">
                <label>Birthdate *</label>
                <input
                  type="date"
                  name="birthDate"
                  value={fieldWorkerForm.birthDate}
                  onChange={handleFieldWorkerChange}
                  className={formErrors.birthDate ? "error" : ""}
                />
                {formErrors.birthDate && (
                  <span className="error-message">{formErrors.birthDate}</span>
                )}
              </div>
              <div className="form-group">
                <label>Birth Place *</label>
                <input
                  type="text"
                  name="birthPlace"
                  value={fieldWorkerForm.birthPlace}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. Makati City"
                  className={formErrors.birthPlace ? "error" : ""}
                />
                {formErrors.birthPlace && (
                  <span className="error-message">{formErrors.birthPlace}</span>
                )}
              </div>
            </div>

            <div className="form-row form-row-4">
              <div className="form-group small">
                <label>Age *</label>
                <input
                  type="number"
                  name="age"
                  value={fieldWorkerForm.age}
                  onChange={handleFieldWorkerChange}
                  min="18"
                  max="65"
                  placeholder="e.g. 25"
                  className={formErrors.age ? "error" : ""}
                />
                {formErrors.age && (
                  <span className="error-message">{formErrors.age}</span>
                )}
              </div>
              <div className="form-group small">
                <label>Sex *</label>
                <select
                  name="sex"
                  value={fieldWorkerForm.sex}
                  onChange={handleFieldWorkerChange}
                  className={formErrors.sex ? "error" : ""}
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
                {formErrors.sex && (
                  <span className="error-message">{formErrors.sex}</span>
                )}
              </div>
              <div className="form-group">
                <label>Civil Status *</label>
                <select
                  name="civilStatus"
                  value={fieldWorkerForm.civilStatus}
                  onChange={handleFieldWorkerChange}
                  className={formErrors.civilStatus ? "error" : ""}
                >
                  <option value="">Select Status</option>
                  <option value="Single">Single</option>
                  <option value="Married">Married</option>
                  <option value="Separated">Separated</option>
                  <option value="Widower">Widower</option>
                </select>
                {formErrors.civilStatus && (
                  <span className="error-message">
                    {formErrors.civilStatus}
                  </span>
                )}
              </div>
            </div>

            <h3>
              <FiMapPin /> Address
            </h3>

            <div className="form-row">
              <div className="form-group">
                <label>Address Line 1 (Street/Building) *</label>
                <input
                  type="text"
                  name="addressLine1"
                  value={fieldWorkerForm.addressLine1}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. Unit 206, The Hope Condo, Chino Roces Ave."
                  className={formErrors.addressLine1 ? "error" : ""}
                />
                {formErrors.addressLine1 && (
                  <span className="error-message">{formErrors.addressLine1}</span>
                )}
              </div>
              <div className="form-group">
                <label>Barangay *</label>
                <input
                  type="text"
                  name="barangay"
                  value={fieldWorkerForm.barangay}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. Pio Del Pilar"
                  className={formErrors.barangay ? "error" : ""}
                />
                {formErrors.barangay && (
                  <span className="error-message">{formErrors.barangay}</span>
                )}
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>City/Municipality *</label>
                <input
                  type="text"
                  name="cityMunicipality"
                  value={fieldWorkerForm.cityMunicipality}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. Makati City"
                  className={formErrors.cityMunicipality ? "error" : ""}
                />
                {formErrors.cityMunicipality && (
                  <span className="error-message">
                    {formErrors.cityMunicipality}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Province</label>
                <input
                  type="text"
                  name="province"
                  value={fieldWorkerForm.province}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. Metro Manila"
                  className={formErrors.province ? "error" : ""}
                />
                {formErrors.province && (
                  <span className="error-message">{formErrors.province}</span>
                )}
              </div>
              <div className="form-group">
                <label>Postal Code *</label>
                <input
                  type="text"
                  name="postalCode"
                  value={fieldWorkerForm.postalCode}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. 1230"
                  className={formErrors.postalCode ? "error" : ""}
                />
                {formErrors.postalCode && (
                  <span className="error-message">{formErrors.postalCode}</span>
                )}
              </div>
            </div>

            <h3>
              <FiMail /> Contact Information
            </h3>

            <div className="form-row">
              <div className="form-group">
                <label>Contact Number (11 digits) *</label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="contactNumber"
                  value={fieldWorkerForm.contactNumber}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. 09171234567"
                  maxLength={11}
                  className={formErrors.contactNumber ? "error" : ""}
                />
                {formErrors.contactNumber && (
                  <span className="error-message">
                    {formErrors.contactNumber}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="emailAddress"
                  value={fieldWorkerForm.emailAddress}
                  onChange={handleFieldWorkerChange}
                  placeholder="e.g. juan.delacruz@email.com"
                  className={formErrors.emailAddress ? "error" : ""}
                />
                {formErrors.emailAddress && (
                  <span className="error-message">
                    {formErrors.emailAddress}
                  </span>
                )}
              </div>
            </div>

            <h3>
              <FiBriefcase /> Job Preferences
            </h3>

            <div className="form-row">
              <div className="form-group">
                <label>Preferred Position *</label>
                <select
                  name="preferredPosition"
                  value={fieldWorkerForm.preferredPosition}
                  onChange={handleFieldWorkerChange}
                  className={formErrors.preferredPosition ? "error" : ""}
                >
                  <option value="">Select Position</option>
                  {fieldWorkerPositions.map((pos, idx) => (
                    <option key={idx} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
                {formErrors.preferredPosition && (
                  <span className="error-message">
                    {formErrors.preferredPosition}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label>Preferred Work Location *</label>
                <select
                  name="preferredLocation"
                  value={fieldWorkerForm.preferredLocation}
                  onChange={handleFieldWorkerChange}
                  className={formErrors.preferredLocation ? "error" : ""}
                >
                  <option value="">Select Location</option>
                  <option value="Davao City">Davao City</option>
                  <option value="Luzon (Metro Manila)">
                    Luzon (Metro Manila)
                  </option>
                  <option value="Cebu">Cebu</option>
                  <option value="Bacolod">Bacolod</option>
                  <option value="Iloilo">Iloilo</option>
                  <option value="Cagayan de Oro">Cagayan de Oro</option>
                  <option value="General Santos">General Santos</option>
                  <option value="Zamboanga">Zamboanga</option>
                  <option value="Any Location">Any Location</option>
                </select>
                {formErrors.preferredLocation && (
                  <span className="error-message">
                    {formErrors.preferredLocation}
                  </span>
                )}
              </div>
            </div>

            {fieldWorkerForm.preferredPosition === "Other Allied Services" && (
              <div className="form-group">
                <label>Please specify the service you are applying for *</label>
                <input
                  type="text"
                  name="otherService"
                  value={fieldWorkerForm.otherService}
                  onChange={handleFieldWorkerChange}
                  placeholder="Enter the specific service you want to apply for"
                  className={formErrors.otherService ? "error" : ""}
                />
                {formErrors.otherService && (
                  <span className="error-message">
                    {formErrors.otherService}
                  </span>
                )}
              </div>
            )}

            <div className="warranty-section">
              <h4>Warranty and Waiver</h4>
              <div className="warranty-text">
                <p>
                  I hereby warrant, affirm, and represent that all information
                  that I have stated in this form is true and correct to the
                  best of my knowledge and belief. In the event that I have
                  willfully misrepresented or submitted false information, I
                  understand that such shall be a ground for the denial of my
                  application.
                </p>
                <p>
                  I hereby waive my right of privacy and allow the cooperative
                  to verify information on my personal background. I agree for
                  my contact numbers to be used for information dissemination,
                  reminders, or collection purposes.
                </p>
              </div>
            </div>

            <div className="agreement-section">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="agreeWarranty"
                  checked={fieldWorkerForm.agreeWarranty}
                  onChange={handleFieldWorkerChange}
                  required
                />
                <span>
                  I have read and agree to the Warranty and Waiver statement
                  above. I certify that all information provided is true and
                  correct.
                </span>
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="agreeDataPrivacy"
                  checked={fieldWorkerForm.agreeDataPrivacy}
                  onChange={handleFieldWorkerChange}
                  required
                />
                <span>I agree to MEDCoop's Data Privacy Policy.</span>
              </label>
            </div>

            {errorMessage && (
              <div className="error-message">
                <FiAlertCircle /> {errorMessage}
              </div>
            )}

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary btn-submit"
                disabled={
                  isSubmitting ||
                  !fieldWorkerForm.agreeWarranty ||
                  !fieldWorkerForm.agreeDataPrivacy
                }
              >
                {isSubmitting ? (
                  <>
                    <FiLoader className="spin" /> Submitting...
                  </>
                ) : (
                  <>
                    Submit Application <FiArrowRight />
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </section>
  );

  // Render Office Staff Form - Multi-step: Step 1 = Personal Info + Resume, Step 2 = IQ Test, Step 3 = Abstract Reasoning, Step 4 = Typing & Personality Test
  const renderOfficeStaffForm = () => (
    <section className="application-form-section section">
      <div className="container">
        <div className="form-top-bar">
          <button className="back-btn" onClick={goBack}>
            <FiArrowLeft />{" "}
            {formStep > 1 ? "Back to Previous Step" : "Back to Positions"}
          </button>
        </div>

        <div className="form-header">
          <div className="form-header-text">
            <h2>
              {selectedPosition
                ? `${selectedPosition.title} Application`
                : "Office Staff Application"}
            </h2>
            <p>
              MEDCoop -{" "}
              {selectedPosition ? selectedPosition.title : "Office Staff"}{" "}
              Position
            </p>
          </div>
        </div>

        {/* Step Progress Indicator */}
        <div className="membership-progress-bar">
          <div className="container">
            <div className="progress-steps">
              {/* Background track line */}
              <div className="progress-track">
                <div
                  className="progress-track-fill"
                  style={{
                    width:
                      formStep === 1
                        ? "0%"
                        : formStep === 2
                          ? "14.28%"
                          : formStep === 3
                            ? "28.57%"
                            : formStep === 4
                              ? "42.85%"
                              : formStep === 5
                                ? "57.14%"
                                : formStep === 6
                                  ? "71.42%"
                                  : formStep === 7
                                    ? "85.71%"
                                    : "100%",
                  }}
                />
              </div>
              <div
                className={`progress-step ${formStep >= 1 ? "active" : ""} ${formStep > 1 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 1 ? <FiCheck /> : "1"}
                </div>
                <span className="step-label">Personal Info</span>
              </div>
              <div
                className={`progress-step ${formStep >= 2 ? "active" : ""} ${formStep > 2 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 2 ? <FiCheck /> : "2"}
                </div>
                <span className="step-label">Typing Test</span>
              </div>
              <div
                className={`progress-step ${formStep >= 3 ? "active" : ""} ${formStep > 3 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 3 ? <FiCheck /> : "3"}
                </div>
                <span className="step-label">Personality Test</span>
              </div>
              <div
                className={`progress-step ${formStep >= 4 ? "active" : ""} ${formStep > 4 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 4 ? <FiCheck /> : "4"}
                </div>
                <span className="step-label">IQ Test</span>
              </div>
              <div
                className={`progress-step ${formStep >= 5 ? "active" : ""} ${formStep > 5 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 5 ? <FiCheck /> : "5"}
                </div>
                <span className="step-label">Abstract Test</span>
              </div>
              <div
                className={`progress-step ${formStep >= 6 ? "active" : ""} ${formStep > 6 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 6 ? <FiCheck /> : "6"}
                </div>
                <span className="step-label">Math Test</span>
              </div>
              <div
                className={`progress-step ${formStep >= 7 ? "active" : ""} ${formStep > 7 ? "completed" : ""}`}
              >
                <div className="progress-dot">
                  {formStep > 7 ? <FiCheck /> : "7"}
                </div>
                <span className="step-label">Essay Writing</span>
              </div>
              <div className={`progress-step ${formStep >= 8 ? "active" : ""}`}>
                <div className="progress-dot">8</div>
                <span className="step-label">Excel Test</span>
              </div>
            </div>
          </div>
        </div>

        <form
          className="hr-application-form"
          onSubmit={handleOfficeStaffSubmit}
        >
          {/* STEP 1: Personal Information + Resume */}
          {formStep === 1 && (
            <div className="form-step">
              <h3>
                <FiUser /> Personal Information
              </h3>
              {Object.keys(formErrors).length > 0 && (
                <div className="form-error-summary">
                  <FiAlertCircle /> Please fill in all required fields before
                  proceeding.
                </div>
              )}

              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={officeStaffForm.fullName}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. Juan A. Dela Cruz"
                    className={formErrors.fullName ? "error" : ""}
                  />
                  {formErrors.fullName && (
                    <span className="error-message">{formErrors.fullName}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Birthdate *</label>
                  <input
                    type="date"
                    name="birthDate"
                    value={officeStaffForm.birthDate}
                    onChange={handleOfficeStaffChange}
                    className={formErrors.birthDate ? "error" : ""}
                  />
                  {formErrors.birthDate && (
                    <span className="error-message">
                      {formErrors.birthDate}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Birthplace *</label>
                  <input
                    type="text"
                    name="birthPlace"
                    value={officeStaffForm.birthPlace}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. Davao City"
                    className={formErrors.birthPlace ? "error" : ""}
                  />
                  {formErrors.birthPlace && (
                    <span className="error-message">
                      {formErrors.birthPlace}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-row form-row-4">
                <div className="form-group small">
                  <label>Age *</label>
                  <input
                    type="number"
                    name="age"
                    value={officeStaffForm.age}
                    onChange={handleOfficeStaffChange}
                    min="18"
                    max="65"
                    placeholder="e.g. 25"
                    className={formErrors.age ? "error" : ""}
                  />
                  {formErrors.age && (
                    <span className="error-message">{formErrors.age}</span>
                  )}
                </div>
                <div className="form-group small">
                  <label>Sex *</label>
                  <select
                    name="sex"
                    value={officeStaffForm.sex}
                    onChange={handleOfficeStaffChange}
                    className={formErrors.sex ? "error" : ""}
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  {formErrors.sex && (
                    <span className="error-message">{formErrors.sex}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Civil Status *</label>
                  <select
                    name="civilStatus"
                    value={officeStaffForm.civilStatus}
                    onChange={handleOfficeStaffChange}
                    className={formErrors.civilStatus ? "error" : ""}
                  >
                    <option value="">Select Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Separated">Separated</option>
                    <option value="Widower">Widower</option>
                  </select>
                  {formErrors.civilStatus && (
                    <span className="error-message">
                      {formErrors.civilStatus}
                    </span>
                  )}
                </div>
              </div>

              <h3>
                <FiMapPin /> Address
              </h3>

              <div className="form-row">
                <div className="form-group">
                  <label>Address Line 1 (Street/Building) *</label>
                  <input
                    type="text"
                    name="addressLine1"
                    value={officeStaffForm.addressLine1}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. Unit 206, The Hope Condo, Chino Roces Ave."
                    className={formErrors.addressLine1 ? "error" : ""}
                  />
                  {formErrors.addressLine1 && (
                    <span className="error-message">{formErrors.addressLine1}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Barangay *</label>
                  <input
                    type="text"
                    name="barangay"
                    value={officeStaffForm.barangay}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. Pio Del Pilar"
                    className={formErrors.barangay ? "error" : ""}
                  />
                  {formErrors.barangay && (
                    <span className="error-message">{formErrors.barangay}</span>
                  )}
                </div>
              </div>

              <div className="form-row form-row-3">
                <div className="form-group">
                  <label>City/Municipality *</label>
                  <input
                    type="text"
                    name="cityMunicipality"
                    value={officeStaffForm.cityMunicipality}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. Makati City"
                    className={formErrors.cityMunicipality ? "error" : ""}
                  />
                  {formErrors.cityMunicipality && (
                    <span className="error-message">
                      {formErrors.cityMunicipality}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Province</label>
                  <input
                    type="text"
                    name="province"
                    value={officeStaffForm.province}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. Metro Manila"
                    className={formErrors.province ? "error" : ""}
                  />
                  {formErrors.province && (
                    <span className="error-message">{formErrors.province}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Postal Code *</label>
                  <input
                    type="text"
                    name="postalCode"
                    value={officeStaffForm.postalCode}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. 1230"
                    className={formErrors.postalCode ? "error" : ""}
                  />
                  {formErrors.postalCode && (
                    <span className="error-message">{formErrors.postalCode}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Contact Number (11 digits) *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="contactNumber"
                    value={officeStaffForm.contactNumber}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. 09171234567"
                    maxLength={11}
                    className={formErrors.contactNumber ? "error" : ""}
                  />
                  {formErrors.contactNumber && (
                    <span className="error-message">
                      {formErrors.contactNumber}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    name="emailAddress"
                    value={officeStaffForm.emailAddress}
                    onChange={handleOfficeStaffChange}
                    placeholder="e.g. juan.delacruz@email.com"
                    className={formErrors.emailAddress ? "error" : ""}
                  />
                  {formErrors.emailAddress && (
                    <span className="error-message">
                      {formErrors.emailAddress}
                    </span>
                  )}
                </div>
              </div>

              {/* Resume Upload */}
              <div className="form-group">
                <label>Upload Resume *</label>
                <div
                  className={`resume-upload-area ${formErrors.resume ? "error" : ""} ${resumeFile ? "has-file" : ""}`}
                >
                  <input
                    type="file"
                    id="resumeUpload"
                    ref={resumeInputRef}
                    accept=".pdf,.doc,.docx"
                    onChange={handleResumeChange}
                    style={{ display: "none" }}
                  />
                  {resumeFile ? (
                    <div className="file-info">
                      <span className="file-status">
                        Resume ready for submission
                      </span>
                      <span className="file-name">{resumeFile.name}</span>
                      <span className="file-size">
                        ({(resumeFile.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <div className="file-actions">
                        <button
                          type="button"
                          className="file-action-btn"
                          onClick={() => openSelectedFile(resumeFile)}
                        >
                          Preview
                        </button>
                        <button
                          type="button"
                          className="file-action-btn file-action-btn-primary"
                          onClick={openResumeFilePicker}
                        >
                          Replace File
                        </button>
                        <button
                          type="button"
                          className="file-action-btn file-action-btn-danger"
                          onClick={clearResumeFile}
                        >
                          Remove File
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="resume-upload-label">
                      <FiUpload className="upload-icon" />
                      <div className="upload-placeholder">
                        <span className="upload-title">
                          Upload your latest resume
                        </span>
                        <span className="upload-hint">
                          Accepted file types: PDF, DOC, DOCX
                        </span>
                        <span className="upload-hint">
                          Maximum file size: 5 MB
                        </span>
                        <button
                          type="button"
                          className="upload-cta"
                          onClick={openResumeFilePicker}
                        >
                          Choose Resume File
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {formErrors.resume && (
                  <span className="error-message">{formErrors.resume}</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextStep}
                >
                  Next Step: Typing Test <FiArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Typing Test */}
          {formStep === 2 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiBook /> Typing Proficiency Test
                </h3>
                <div className="test-badge">Test 1</div>
              </div>

              <div className="iq-test-instructions">
                <FiAlertCircle />
                <div>
                  <p>
                    This is a 1-minute typing test. Type the text shown below as
                    quickly and accurately as possible. Your score will be
                    calculated based on your typing speed and accuracy.
                  </p>
                </div>
              </div>

              {formErrors.typingTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.typingTest}
                </div>
              )}

              {/* Typing Test Area */}
              <div className="typing-test-container">
                {(typingTestStatus === "idle" ||
                  typingTestStatus === "ready" ||
                  typingTestStatus === "running") && (
                  <div className="typing-test-active">
                    <div className="typing-test-timer">
                      <FiClock />
                      {typingTestStatus === "idle" ||
                      typingTestStatus === "ready" ? (
                        <span className="timer-ready">
                          Start typing to begin!
                        </span>
                      ) : (
                        <span
                          className={
                            typingTimeLeft <= 10 ? "timer-warning" : ""
                          }
                        >
                          {Math.floor(typingTimeLeft / 60)}:
                          {(typingTimeLeft % 60).toString().padStart(2, "0")}
                        </span>
                      )}
                    </div>

                    <div className="typing-test-text-display">
                      {typingTestText.split("").map((char, index) => {
                        let className = "";
                        if (index < typingUserInput.length) {
                          className =
                            typingUserInput[index] === char
                              ? "correct"
                              : "incorrect";
                        } else if (index === typingUserInput.length) {
                          className = "current";
                        }
                        return (
                          <span key={index} className={className}>
                            {char}
                          </span>
                        );
                      })}
                    </div>

                    <textarea
                      ref={typingInputRef}
                      className="typing-test-input"
                      value={typingUserInput}
                      onChange={handleTypingInput}
                      placeholder="Start typing here to begin the timer..."
                      autoFocus
                    />

                    <div className="typing-test-live-stats">
                      <span>Characters: {typingUserInput.length}</span>
                      <span>
                        Words: {Math.floor(typingUserInput.length / 5)}
                      </span>
                    </div>

                    {typingTestStatus === "running" && (
                      <button
                        type="button"
                        className="btn btn-outline btn-finish-early"
                        onClick={handleFinishEarly}
                      >
                        Finish Early
                      </button>
                    )}
                  </div>
                )}

                {typingTestStatus === "completed" && typingResults && (
                  <div
                    className={`typing-test-results ${showResultsAnimation ? "animate" : ""}`}
                  >
                    <div className="results-header">
                      <FiAward className="results-icon" />
                      <h3>Your Test Score</h3>
                    </div>

                    <div className="results-formula">
                      <div className="formula-item speed">
                        <div className="formula-circle animate-circle">
                          <span className="formula-value">
                            {animatedResults.grossWpm}
                          </span>
                          <span className="formula-label">WPM</span>
                        </div>
                        <span className="formula-sublabel">Typing Speed</span>
                      </div>

                      <span className="formula-operator">×</span>

                      <div className="formula-item accuracy">
                        <div className="formula-circle animate-circle">
                          <span className="formula-value">
                            {animatedResults.accuracy}%
                          </span>
                          <span className="formula-label">
                            {typingResults.errors === 0 ||
                            typingResults.accuracy === 100
                              ? "Perfect!"
                              : `${typingResults.errors} error${typingResults.errors === 1 ? "" : "s"}`}
                          </span>
                        </div>
                        <span className="formula-sublabel">Accuracy</span>
                      </div>

                      <span className="formula-operator">=</span>

                      <div className="formula-item net-speed">
                        <div
                          className="formula-circle highlight animate-circle"
                          style={{
                            borderColor: getSpeedCategory(typingResults.netWpm)
                              .color,
                          }}
                        >
                          <span
                            className="formula-value"
                            style={{
                              color: getSpeedCategory(typingResults.netWpm)
                                .color,
                            }}
                          >
                            {animatedResults.netWpm}
                          </span>
                          <span className="formula-label">WPM</span>
                        </div>
                        <span className="formula-sublabel">Net Speed</span>
                      </div>
                    </div>

                    <div className="speed-scale">
                      <div className="scale-bar">
                        <div className="scale-section slow">Slow</div>
                        <div className="scale-section average">Average</div>
                        <div className="scale-section fluent">Fluent</div>
                        <div className="scale-section fast">Fast</div>
                        <div className="scale-section pro">Pro</div>
                      </div>
                      <div className="scale-numbers">
                        <span>0</span>
                        <span>25</span>
                        <span>45</span>
                        <span>65</span>
                        <span>85</span>
                        <span>100+</span>
                      </div>
                      <div
                        className={`scale-indicator ${showResultsAnimation ? "animate-indicator" : ""}`}
                        style={{
                          left: `${Math.min(animatedResults.netWpm, 100)}%`,
                          backgroundColor: getSpeedCategory(
                            typingResults.netWpm,
                          ).color,
                        }}
                      >
                        <span className="indicator-value">
                          {animatedResults.netWpm}
                        </span>
                      </div>
                    </div>

                    <div
                      className={`speed-category ${showResultsAnimation ? "animate-badge" : ""}`}
                      style={{
                        backgroundColor: getSpeedCategory(typingResults.netWpm)
                          .color,
                      }}
                    >
                      <FiStar />
                      <span>
                        Your typing speed is:{" "}
                        <strong>
                          {getSpeedCategory(typingResults.netWpm).label}
                        </strong>
                      </span>
                    </div>

                    <div className="results-actions">
                      <span className="results-satisfied">
                        <FiCheck /> Satisfied with your score? Proceed to the
                        next step.
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Only show form actions when test is completed */}
              {typingTestStatus === "completed" && typingResults && (
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline btn-personality-nav"
                    onClick={retakeTypingTest}
                  >
                    <FiArrowLeft /> Retake Test
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={nextStep}
                  >
                    Next Step: Personality Test <FiArrowRight />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Personality Test (Cattell's 16PF) */}
          {formStep === 3 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiBook /> Personality Test
                </h3>
                <div className="test-badge">Test 2</div>
              </div>

              {formErrors.personalityTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.personalityTest}
                </div>
              )}

              {/* Running State - Questions (starts immediately after modal) */}
              {personalityTestStatus === "running" && (
                <div className="personality-test-active">
                  <div className="personality-progress">
                    <div className="progress-text">
                      Question {personalityCurrentQuestion + 1} of{" "}
                      {personalityQuestions.length}
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{
                          width: `${((personalityCurrentQuestion + 1) / personalityQuestions.length) * 100}%`,
                        }}
                      />
                    </div>
                    <div className="progress-answered">
                      {getAnsweredQuestionsCount()} of{" "}
                      {personalityQuestions.length} answered
                    </div>
                  </div>

                  {personalityQuestions[personalityCurrentQuestion] && (
                    <div className="personality-question-card">
                      <div className="question-number">
                        Q{personalityCurrentQuestion + 1}
                      </div>
                      <div className="question-text">
                        {personalityQuestions[personalityCurrentQuestion].text}
                      </div>

                      <div className="personality-options">
                        {[
                          { value: 1, label: "Strongly Disagree" },
                          { value: 2, label: "Disagree" },
                          { value: 3, label: "Neither Agree nor Disagree" },
                          { value: 4, label: "Agree" },
                          { value: 5, label: "Strongly Agree" },
                        ].map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`personality-option ${personalityAnswers[personalityQuestions[personalityCurrentQuestion]?.id] === option.value ? "selected" : ""}`}
                            onClick={() =>
                              handlePersonalityAnswer(
                                personalityQuestions[personalityCurrentQuestion]
                                  ?.id,
                                option.value,
                              )
                            }
                          >
                            <span className="option-value">{option.value}</span>
                            <span className="option-label">{option.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="personality-navigation">
                    <button
                      type="button"
                      className="btn btn-outline btn-personality-nav"
                      onClick={goToPreviousPersonalityQuestion}
                      disabled={personalityCurrentQuestion === 0}
                    >
                      <FiChevronLeft /> Previous
                    </button>

                    {personalityCurrentQuestion <
                    personalityQuestions.length - 1 ? (
                      <button
                        type="button"
                        className="btn btn-outline btn-personality-nav"
                        onClick={goToNextPersonalityQuestion}
                      >
                        Next <FiChevronRight />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={finishPersonalityTest}
                      >
                        <FiCheck /> Finish Test
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Completed State - Results */}
              {personalityTestStatus === "completed" && personalityResults && (
                <div
                  className={`personality-test-results ${showPersonalityResultsAnimation ? "animate" : ""}`}
                >
                  {/* Results Header */}
                  <div className="results-intro">
                    <p>
                      The test is complete. Here are your results; scores are
                      between 0 and 100.
                    </p>
                  </div>

                  {/* Factor Results Table */}
                  <div className="personality-factors-table">
                    <div className="factors-header">
                      <span className="header-trait">Trait</span>
                      <span className="header-score">Score</span>
                      <span className="header-percentile">Percentile</span>
                    </div>

                    {Object.entries(personalityResults.factors).map(
                      ([factorKey, factor]) => (
                        <div key={factorKey} className="factor-row">
                          <div className="factor-trait">
                            <span className="factor-name">
                              {factor.factorInfo.name}
                            </span>
                          </div>
                          <div className="factor-score-section">
                            <div className="score-value">
                              {factor.average.toFixed(1)}
                            </div>
                            <div className="score-bar-container">
                              <div
                                className="score-bar"
                                style={{
                                  width: showPersonalityResultsAnimation
                                    ? `${factor.percentage}%`
                                    : "0%",
                                  backgroundColor: "#c4813a",
                                }}
                              />
                            </div>
                            <div className="factor-description">
                              {factor.factorInfo.name} is{" "}
                              {factor.isHigh
                                ? factor.factorInfo.highDescription.toLowerCase()
                                : factor.factorInfo.lowDescription.toLowerCase()}
                            </div>
                          </div>
                          <div className="factor-percentile">/</div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              {/* Form actions - only show when test is completed */}
              {personalityTestStatus === "completed" && personalityResults && (
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline btn-personality-nav"
                    onClick={retakePersonalityTest}
                  >
                    <FiArrowLeft /> Retake Test
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={nextStep}
                  >
                    Next Step: IQ Test <FiArrowRight />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* STEP 4: MEDC IQ Test */}
          {formStep === 4 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiBook /> MEDC IQ Test
                </h3>
                <div className="test-badge">Test 3</div>
              </div>
              <div className="iq-test-instructions">
                <FiAlertCircle />
                <div>
                  <p>
                    The IQ test is designed to assess the cognitive abilities
                    and problem-solving skills of applicants. This test will
                    evaluate your logical reasoning, pattern recognition,
                    numerical aptitude, and ability to understand and apply
                    information quickly. The goal is to better understand your
                    intellectual strengths and potential for success in the role
                    you are applying for.
                  </p>
                </div>
              </div>

              {formErrors.iqTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.iqTest}
                </div>
              )}

              <div className="iq-questions-list">
                {iqTestAttemptQuestions.map((q, index) => (
                  <div
                    key={q.id}
                    className={`iq-question ${iqTestAnswers[q.id] !== undefined ? "answered" : ""}`}
                  >
                    <p className="question-number">
                      Question {index + 1} of {iqTestAttemptQuestions.length}
                    </p>
                    <p className="question-text">{q.question}</p>
                    <div className="question-options">
                      {q.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className={`iq-option-label ${iqTestAnswers[q.id] === optIndex ? "selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`iq-${q.id}`}
                            value={optIndex}
                            checked={iqTestAnswers[q.id] === optIndex}
                            onChange={() => handleIqTestAnswer(q.id, optIndex)}
                          />
                          <span className="option-letter">
                            {String.fromCharCode(65 + optIndex)}
                          </span>
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="iq-test-progress">
                <FiClock />
                <span>
                  {Object.keys(iqTestAnswers).length} of{" "}
                  {iqTestAttemptQuestions.length} questions answered
                </span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={goBack}
                >
                  <FiArrowLeft /> Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextStep}
                >
                  Next Step: Abstract Reasoning <FiArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: Abstract Reasoning Test */}
          {formStep === 5 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiBook /> Abstract Reasoning Test
                </h3>
                <div className="test-badge">Test 4</div>
              </div>
              <div className="iq-test-instructions">
                <FiAlertCircle />
                <div>
                  <p>
                    This test measures your ability to identify patterns and
                    solve non-verbal problems using abstract symbols and
                    sequences.
                  </p>
                </div>
              </div>

              {formErrors.abstractTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.abstractTest}
                </div>
              )}

              <div className="iq-questions-list">
                {abstractAttemptQuestions.map((q, index) => (
                  <div
                    key={q.id}
                    className={`iq-question ${abstractTestAnswers[q.id] !== undefined ? "answered" : ""}`}
                  >
                    <p className="question-number">
                      Question {index + 1} of {abstractAttemptQuestions.length}
                    </p>
                    <p className="question-text">{q.question}</p>
                    <div className="question-options">
                      {q.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className={`iq-option-label ${abstractTestAnswers[q.id] === optIndex ? "selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`abstract-${q.id}`}
                            value={optIndex}
                            checked={abstractTestAnswers[q.id] === optIndex}
                            onChange={() =>
                              handleAbstractTestAnswer(q.id, optIndex)
                            }
                          />
                          <span className="option-letter">
                            {String.fromCharCode(65 + optIndex)}
                          </span>
                          <span>{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="iq-test-progress">
                <FiClock />
                <span>
                  {Object.keys(abstractTestAnswers).length} of{" "}
                  {abstractAttemptQuestions.length} questions answered
                </span>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={goBack}
                >
                  <FiArrowLeft /> Back
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={nextStep}
                >
                  Next Step: Math Test <FiArrowRight />
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: Mathematical Analogy Test */}
          {formStep === 6 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiBook /> Mathematical Analogy Test
                </h3>
                <div className="test-badge">Test 5</div>
              </div>

              {/* Timer Display */}
              {mathTestStatus === "running" && (
                <div
                  className={`math-test-timer ${mathTestTimeLeft <= 60 ? "warning" : ""}`}
                >
                  <FiClock />
                  <span>
                    Time Remaining: {formatMathTime(mathTestTimeLeft)}
                  </span>
                </div>
              )}

              <div className="iq-test-instructions">
                <FiAlertCircle />
                <div>
                  <p>
                    Convert the given time to total hours and minutes. You have
                    15 minutes to complete this test. You may use your
                    calculator.
                  </p>
                </div>
              </div>

              {formErrors.mathTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.mathTest}
                </div>
              )}

              {/* Test Not Started State */}
              {mathTestStatus === "idle" && (
                <div className="math-test-ready">
                  <p>
                    Click the button below to start the test. The 15-minute
                    timer will begin immediately.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={startMathTest}
                  >
                    Start Test <FiArrowRight />
                  </button>
                </div>
              )}

              {/* Test Running State */}
              {mathTestStatus === "running" && (
                <>
                  <div className="math-time-table">
                    <div className="math-table-header">
                      <div className="math-col time-in">TIME IN</div>
                      <div className="math-col time-out">TIME OUT</div>
                      <div className="math-col hours">
                        CONVERT TO TOTAL NUMBER OF HOURS
                      </div>
                      <div className="math-col minutes">
                        CONVERT TO TOTAL NUMBER OF MINUTES
                      </div>
                    </div>
                    {mathTimeQuestions.map((q, index) => (
                      <div key={q.id} className="math-table-row">
                        <div className="math-col time-in">{q.timeIn}</div>
                        <div className="math-col time-out">{q.timeOut}</div>
                        <div className="math-col hours">
                          <div className="hours-input-group">
                            <input
                              type="text"
                              placeholder="Hrs"
                              value={mathTestAnswers[q.id]?.hours || ""}
                              onChange={(e) =>
                                handleMathTestAnswer(
                                  q.id,
                                  "hours",
                                  e.target.value,
                                )
                              }
                              className="math-input"
                            />
                            <span className="separator">:</span>
                            <input
                              type="text"
                              placeholder="Min"
                              value={mathTestAnswers[q.id]?.minutes || ""}
                              onChange={(e) =>
                                handleMathTestAnswer(
                                  q.id,
                                  "minutes",
                                  e.target.value,
                                )
                              }
                              className="math-input"
                            />
                          </div>
                        </div>
                        <div className="math-col minutes">
                          <input
                            type="text"
                            placeholder="Total Minutes"
                            value={mathTestAnswers[q.id]?.totalMinutes || ""}
                            onChange={(e) =>
                              handleMathTestAnswer(
                                q.id,
                                "totalMinutes",
                                e.target.value,
                              )
                            }
                            className="math-input total-minutes"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="math-test-progress">
                    <FiClock />
                    <span>
                      {
                        Object.keys(mathTestAnswers).filter(
                          (k) =>
                            mathTestAnswers[k]?.hours ||
                            mathTestAnswers[k]?.totalMinutes,
                        ).length
                      }{" "}
                      of {mathTimeQuestions.length} rows answered
                    </span>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={finishMathTest}
                    >
                      <FiCheck /> Finish Test
                    </button>
                  </div>
                </>
              )}

              {/* Test Completed State */}
              {mathTestStatus === "completed" && mathTestResults && (
                <>
                  <div className="math-test-results">
                    <div className="results-header">
                      <FiCheck className="results-icon" />
                      <h3>Test Completed!</h3>
                    </div>
                    <div className="results-score">
                      <span className="score-value">
                        {mathTestResults.correct}/{mathTestResults.total}
                      </span>
                      <span className="score-label">
                        Correct Answers ({mathTestResults.percentage}%)
                      </span>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={goBack}
                    >
                      <FiArrowLeft /> Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={nextStep}
                    >
                      Next Step: Essay Writing <FiArrowRight />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 7: Essay Writing Test */}
          {formStep === 7 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiBook /> Essay Writing Test
                </h3>
                <div className="test-badge">Test 6</div>
              </div>

              {/* Timer Display */}
              {essayTestStatus === "running" && (
                <div
                  className={`essay-test-timer ${essayTimeLeft <= 60 ? "warning" : ""}`}
                >
                  <FiClock />
                  <span>Time Remaining: {formatEssayTime(essayTimeLeft)}</span>
                </div>
              )}

              {formErrors.essayTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.essayTest}
                </div>
              )}

              {/* Test Not Started State */}
              {essayTestStatus === "idle" && (
                <div className="essay-test-ready">
                  <div className="essay-prompt">
                    <h4>Essay Topic:</h4>
                    <p className="topic-text">
                      "How was your family upbringing affected your views in
                      life?"
                    </p>
                  </div>
                  <div className="essay-requirements">
                    <p>
                      <strong>Requirements:</strong>
                    </p>
                    <ul>
                      <li>Minimum of 200 words</li>
                      <li>20 minutes to complete</li>
                      <li>Write honestly and thoughtfully</li>
                    </ul>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={startEssayTest}
                  >
                    Start Writing <FiArrowRight />
                  </button>
                </div>
              )}

              {/* Test Running State */}
              {essayTestStatus === "running" && (
                <>
                  <div className="essay-prompt-header">
                    <h4>Essay Topic:</h4>
                    <p>
                      "How was your family upbringing affected your views in
                      life?"
                    </p>
                  </div>

                  <div className="essay-writing-area">
                    <textarea
                      className="essay-textarea"
                      placeholder="Start writing your essay here..."
                      value={essayContent}
                      onChange={handleEssayChange}
                      rows={15}
                    />
                    <div className="essay-word-count">
                      <FiBook />
                      <span
                        className={essayWordCount >= 200 ? "valid" : "invalid"}
                      >
                        {essayWordCount} / 200 words minimum
                      </span>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={finishEssayTest}
                    >
                      <FiCheck /> Finish Test
                    </button>
                  </div>
                </>
              )}

              {/* Test Completed State */}
              {essayTestStatus === "completed" && (
                <>
                  <div className="essay-test-results">
                    <div className="results-header">
                      <FiCheck className="results-icon" />
                      <h3>Essay Submitted!</h3>
                    </div>
                    <div className="results-stats">
                      <div className="stat-item">
                        <span className="stat-label">Word Count</span>
                        <span className="stat-value">
                          {essayWordCount} words
                        </span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Time Used</span>
                        <span className="stat-value">
                          {formatEssayTime(20 * 60 - essayTimeLeft)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="essay-preview">
                    <h4>Your Essay:</h4>
                    <div className="essay-content-preview">{essayContent}</div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={goBack}
                    >
                      <FiArrowLeft /> Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={nextStep}
                    >
                      Next Step: Excel Test <FiArrowRight />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 8: Excel Proficiency Test */}
          {formStep === 8 && (
            <div className="form-step">
              <div className="iq-test-header">
                <h3>
                  <FiFileText /> Excel Proficiency Test
                </h3>
                <div className="test-badge">Test 7</div>
              </div>

              {/* Timer Display */}
              {excelTestStatus === "running" && (
                <div
                  className={`excel-test-timer ${excelTimeLeft <= 60 ? "warning" : ""}`}
                >
                  <FiClock />
                  <span>Time Remaining: {formatExcelTime(excelTimeLeft)}</span>
                </div>
              )}

              {formErrors.excelTest && (
                <div className="form-error-summary">
                  <FiAlertCircle /> {formErrors.excelTest}
                </div>
              )}

              {/* Test Not Started State */}
              {excelTestStatus === "idle" && (
                <div className="excel-test-ready">
                  <div className="excel-instructions">
                    <h4>Instructions:</h4>
                    <p>
                      Create this <strong>Client Billing Report</strong> table
                      in Microsoft Excel (or compatible software). The TOTAL
                      column and TOTAL row should use{" "}
                      <strong>SUM formulas</strong>.
                    </p>
                    <div className="excel-table-sample excel-style">
                      <table>
                        <thead>
                          <tr>
                            <th>CLIENT</th>
                            <th>I</th>
                            <th>II</th>
                            <th>III</th>
                            <th>TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>A</td>
                            <td>10,500</td>
                            <td>8,500</td>
                            <td>9,450</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>B</td>
                            <td>11,000</td>
                            <td>7,010</td>
                            <td>10,600</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>C</td>
                            <td>6,750</td>
                            <td>8,500</td>
                            <td>9,800</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>D</td>
                            <td>14,005</td>
                            <td>7,100</td>
                            <td>14,100</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>E</td>
                            <td>25,000</td>
                            <td>24,000</td>
                            <td>23,500</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr className="total-row">
                            <td>TOTAL</td>
                            <td className="formula-cell"></td>
                            <td className="formula-cell"></td>
                            <td className="formula-cell"></td>
                            <td className="formula-cell"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="excel-requirements">
                      <p>
                        <strong>Requirements:</strong>
                      </p>
                      <ul>
                        <li>
                          The <strong>TOTAL column</strong> must use SUM formula
                          to add columns I + II + III for each client
                        </li>
                        <li>
                          The <strong>TOTAL row</strong> must use SUM formula to
                          add all values in each column
                        </li>
                        <li>Save your file as .xlsx, .xls, or .ods format</li>
                        <li>Time limit: 15 minutes</li>
                      </ul>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={startExcelTest}
                  >
                    Start Test <FiArrowRight />
                  </button>
                </div>
              )}

              {/* Test Running State */}
              {excelTestStatus === "running" && (
                <>
                  <div className="excel-task-reminder">
                    <h4>Your Task: Create this table in Excel</h4>
                    <div className="excel-table-sample excel-style running-state">
                      <table>
                        <thead>
                          <tr>
                            <th>CLIENT</th>
                            <th>I</th>
                            <th>II</th>
                            <th>III</th>
                            <th>TOTAL</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>A</td>
                            <td>10,500</td>
                            <td>8,500</td>
                            <td>9,450</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>B</td>
                            <td>11,000</td>
                            <td>7,010</td>
                            <td>10,600</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>C</td>
                            <td>6,750</td>
                            <td>8,500</td>
                            <td>9,800</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>D</td>
                            <td>14,005</td>
                            <td>7,100</td>
                            <td>14,100</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr>
                            <td>E</td>
                            <td>25,000</td>
                            <td>24,000</td>
                            <td>23,500</td>
                            <td className="formula-cell"></td>
                          </tr>
                          <tr className="total-row">
                            <td>TOTAL</td>
                            <td className="formula-cell"></td>
                            <td className="formula-cell"></td>
                            <td className="formula-cell"></td>
                            <td className="formula-cell"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="formula-reminder">
                      <strong>Remember:</strong> Use SUM formulas for TOTAL
                      column and TOTAL row!
                    </p>
                  </div>

                  <div className="excel-upload-section">
                    <h4>Upload Your Completed Excel File:</h4>
                    <div className="file-upload-area excel-file-upload">
                      <input
                        type="file"
                        id="excelFile"
                        accept=".xlsx,.xls,.ods"
                        onChange={handleExcelFileChange}
                        className="file-input"
                      />
                      <label htmlFor="excelFile" className="file-label">
                        <FiUpload />
                        <span>
                          {excelFile
                            ? excelFile.name
                            : "Click to upload Excel file (.xlsx, .xls, .ods)"}
                        </span>
                      </label>
                      {excelFile && (
                        <div className="file-info">
                          <FiCheck className="file-success" />
                          <span>File selected: {excelFile.name}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={finishExcelTest}
                      disabled={!excelFile}
                    >
                      <FiCheck /> Finish Test
                    </button>
                  </div>
                </>
              )}

              {/* Test Completed State */}
              {excelTestStatus === "completed" && (
                <>
                  <div className="excel-test-results">
                    <div className="results-header">
                      <FiCheck className="results-icon" />
                      <h3>Excel Test Submitted!</h3>
                    </div>
                    <div className="results-stats">
                      <div className="stat-item">
                        <span className="stat-label">File Uploaded</span>
                        <span className="stat-value">{excelFile?.name}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Time Used</span>
                        <span className="stat-value">
                          {formatExcelTime(15 * 60 - excelTimeLeft)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="warranty-section">
                    <h4>Warranty and Waiver</h4>
                    <div className="warranty-text">
                      <p>
                        I hereby warrant, affirm, and represent that all
                        information that I have stated in this form is true and
                        correct to the best of my knowledge and belief. In the
                        event that I have willfully misrepresented or submitted
                        false information, I understand that such shall be a
                        ground for the denial of my application.
                      </p>
                    </div>
                  </div>

                  <div className="agreement-section">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="agreeWarranty"
                        checked={officeStaffForm.agreeWarranty}
                        onChange={handleOfficeStaffChange}
                        required
                      />
                      <span>
                        I certify that all information provided is true and
                        correct.
                      </span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="agreeDataPrivacy"
                        checked={officeStaffForm.agreeDataPrivacy}
                        onChange={handleOfficeStaffChange}
                        required
                      />
                      <span>I agree to MEDCoop's Data Privacy Policy.</span>
                    </label>
                  </div>

                  {errorMessage && (
                    <div
                      className="error-message"
                      style={{ marginTop: "1rem" }}
                    >
                      <FiAlertCircle /> {errorMessage}
                    </div>
                  )}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={goBack}
                    >
                      <FiArrowLeft /> Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary btn-submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          Submitting... <FiLoader className="spinner" />
                        </>
                      ) : (
                        <>
                          Submit Application <FiCheck />
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </form>
      </div>
    </section>
  );

  // Render Success Message
  const renderSuccessMessage = () => (
    <section className="success-message section">
      <div className="container">
        <div className="success-content">
          <div className="success-icon">
            <FiCheck />
          </div>
          {selectedPath === "fieldWorker" ? (
            <>
              <h2>Reservation Confirmed!</h2>
              {applicationId && (
                <p className="application-id">
                  <strong>Reservation Reference ID:</strong> {applicationId}
                </p>
              )}
              <p>
                Thank you for your interest in joining MEDCoop as a Field
                Service Worker. Your application has been received and you are
                now scheduled for processing.
              </p>

              <div className="reservation-details">
                <h4>
                  <FiCalendar /> Visit Our Office
                </h4>
                <p className="office-visit-info">
                  Please proceed to any MEDCoop branch office during office
                  hours (Monday - Saturday, 8:00 AM - 5:00 PM) to complete your
                  application process.
                </p>
              </div>

              <div className="requirements-reminder reservation-style">
                <h4>
                  <FiFileText /> Documents to Bring
                </h4>
                <p>
                  Please prepare and bring the following documents when you
                  visit our office:
                </p>
                <ul className="documents-checklist">
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>Valid Government ID</strong> - (SSS, PhilHealth,
                    Pag-IBIG, TIN, Voter's ID, Driver's License, Passport, or
                    any valid ID)
                  </li>
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>Birth Certificate</strong> - Original or certified
                    true copy from PSA/NSO
                  </li>
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>NBI / Police Clearance</strong> - Must be valid and
                    not expired
                  </li>
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>Barangay Clearance</strong> - From your current
                    residence
                  </li>
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>2x2 ID Photos</strong> - At least 2 pieces, white
                    background
                  </li>
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>Medical Certificate</strong> - From any accredited
                    clinic/hospital (if available)
                  </li>
                  <li>
                    <FiCheck className="check-icon" />{" "}
                    <strong>High School/College Diploma</strong> - Or Transcript
                    of Records (if available)
                  </li>
                </ul>
              </div>

              <div className="important-reminders">
                <h4>
                  <FiAlertCircle /> Important Reminders
                </h4>
                <ul>
                  <li>
                    Bring original documents for verification and photocopies
                    for submission
                  </li>
                  <li>
                    Wear appropriate attire (smart casual or business casual)
                  </li>
                  <li>Arrive at least 15 minutes before office hours</li>
                  <li>
                    Save your Reference ID: <strong>{applicationId}</strong> for
                    tracking
                  </li>
                </ul>
              </div>

              <div className="office-locations">
                <h4>
                  <FiMapPin /> MEDCoop Branch Offices
                </h4>
                <p>
                  <strong>Main Office:</strong> Davao City
                </p>
                <p>Contact: (082) 123-4567 | Mobile: 0917-123-4567</p>
              </div>
            </>
          ) : (
            <>
              <h2>Application Submitted Successfully!</h2>
              {applicationId && (
                <p className="application-id">
                  <strong>Application Reference ID:</strong> {applicationId}
                </p>
              )}
              <p>
                Thank you for applying to MEDCoop. Our HR team will review your
                application and contact you within 3-5 business days. A
                notification has been sent to our HR department.
              </p>
              <div className="success-details">
                <h4>What happens next?</h4>
                <ul>
                  <li>
                    Our HR team will review your application and test results
                  </li>
                  <li>
                    You will receive a call or email for interview scheduling
                  </li>
                  <li>Attend the interview at the scheduled date and time</li>
                </ul>
              </div>
              <p className="success-note">
                Please wait for our HR team to contact you for the next steps.
              </p>
            </>
          )}
          <button
            className="btn btn-primary"
            onClick={handleBackToCareerOptions}
          >
            <FiArrowLeft /> Back to Career Options
          </button>
        </div>
      </div>
    </section>
  );

  return (
    <div
      className={`membership-page careers-page ${selectedPath !== null ? "form-mode" : ""}`}
    >
      {/* Only show hero section when not in application form */}
      {selectedPath === null && (
        <section className="membership-hero careers-hero">
          <div className="container">
            <h1>{t("careers.title")}</h1>
            <p>{t("careers.subtitle")}</p>
          </div>
        </section>
      )}

      {submissionStatus === "success" ? (
        renderSuccessMessage()
      ) : selectedPath === null ? (
        <>
          {renderHiringSection()}

          <section className="benefits-overview section">
            <div className="container">
              <div className="section-header scroll-fade-up">
                <h2 className="section-title">Why Work With Us?</h2>
                <p className="section-subtitle">
                  Benefits you'll enjoy when you join MEDCoop
                </p>
              </div>
              <div className="benefits-overview-grid scroll-fade-up">
                {benefits.map((benefit, index) => (
                  <div className="benefit-overview-card" key={index}>
                    <div className="benefit-overview-icon">{benefit.icon}</div>
                    <h4>{benefit.title}</h4>
                    <p>{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : selectedPath === "fieldWorker" ? (
        renderFieldWorkerForm()
      ) : (
        renderOfficeStaffForm()
      )}

      {/* Exam Introduction Modal */}
      {showExamIntroModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal exam-intro-modal">
            <div className="iq-test-modal-header">
              <FiFileText className="modal-icon" />
              <h2>Assessment Tests Overview</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-intro">
                <p>
                  Welcome! Before we proceed, you will need to complete{" "}
                  <strong>7 assessment tests</strong> as part of your
                  application for Office Staff position.
                </p>
              </div>
              <div className="exam-list">
                <h4>The following tests will be administered:</h4>
                <div className="exam-items">
                  <div className="exam-item">
                    <span className="exam-number">1</span>
                    <div className="exam-info">
                      <strong>Typing Proficiency Test</strong>
                      <span>
                        1 minute - Measures your typing speed and accuracy
                      </span>
                    </div>
                  </div>
                  <div className="exam-item">
                    <span className="exam-number">2</span>
                    <div className="exam-info">
                      <strong>Personality Test (16PF)</strong>
                      <span>
                        No time limit - Assesses your personality traits
                      </span>
                    </div>
                  </div>
                  <div className="exam-item">
                    <span className="exam-number">3</span>
                    <div className="exam-info">
                      <strong>IQ Test</strong>
                      <span>
                        No time limit - Tests logical reasoning abilities
                      </span>
                    </div>
                  </div>
                  <div className="exam-item">
                    <span className="exam-number">4</span>
                    <div className="exam-info">
                      <strong>Abstract Reasoning Test</strong>
                      <span>
                        No time limit - Evaluates pattern recognition skills
                      </span>
                    </div>
                  </div>
                  <div className="exam-item">
                    <span className="exam-number">5</span>
                    <div className="exam-info">
                      <strong>Math Analogy Test</strong>
                      <span>15 minutes - Tests mathematical reasoning</span>
                    </div>
                  </div>
                  <div className="exam-item">
                    <span className="exam-number">6</span>
                    <div className="exam-info">
                      <strong>Essay Writing Test</strong>
                      <span>
                        20 minutes - Evaluates written communication skills
                      </span>
                    </div>
                  </div>
                  <div className="exam-item">
                    <span className="exam-number">7</span>
                    <div className="exam-info">
                      <strong>Excel Proficiency Test</strong>
                      <span>
                        15 minutes - Tests spreadsheet and formula skills
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-note">
                <FiAlertCircle className="note-icon" />
                <p>
                  Please ensure you have a stable internet connection and are in
                  a quiet environment before starting. Once you begin, complete
                  all tests in one session.
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleProceedToExams}
              >
                I Understand, Proceed to Tests <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Typing Test Instructions Modal */}
      {showTypingTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiClock className="modal-icon" />
              <h2>Typing Proficiency Test</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message">
                <FiClock className="no-timer-icon" />
                <p className="no-timer-text">
                  You have <strong>1 minute</strong> to type as much as
                  possible!
                </p>
              </div>
              <div className="modal-instructions">
                <h4>Instructions:</h4>
                <ul>
                  <li>
                    Type the displayed text as quickly and accurately as
                    possible
                  </li>
                  <li>Timer starts when you begin typing</li>
                  <li>Your Net Speed = Typing Speed × Accuracy</li>
                  <li>You can retake the test if you're not satisfied</li>
                </ul>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>Focus on accuracy first, speed will follow. Good luck!</p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowTypingTestModal(false);
                  startTypingTest();
                }}
              >
                I'm Ready, Let's Start! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* IQ Test Instructions Modal */}
      {showIqTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiBook className="modal-icon" />
              <h2>IQ Test Instructions</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message">
                <FiClock className="no-timer-icon" />
                <p className="no-timer-text">
                  Don't worry, there is no timer in this test!
                </p>
              </div>
              <div className="modal-instructions">
                <h4>Instructions:</h4>
                <ul>
                  <li>Read each question carefully before answering</li>
                  <li>Select the best answer from the given options</li>
                  <li>You must answer all questions to proceed</li>
                  <li>Take your time and do your best!</li>
                </ul>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>
                  Believe in yourself and give it your best shot. Good luck!
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowIqTestModal(false)}
              >
                I'm Ready, Let's Start! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Personality Test Instructions Modal */}
      {showPersonalityTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiBook className="modal-icon" />
              <h2>Personality Test</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message">
                <FiClock className="no-timer-icon" />
                <p className="no-timer-text">
                  Take your time - there's no timer!
                </p>
              </div>
              <div className="modal-intro">
                <p>
                  This is{" "}
                  <strong>Cattell's 16 Personality Factors (16PF)</strong>{" "}
                  assessment - a scientifically developed personality test that
                  measures 16 primary traits.
                </p>
              </div>
              <div className="modal-instructions">
                <h4>Procedure:</h4>
                <ul>
                  <li>
                    This test consists of <strong>164 statements</strong> about
                    yourself
                  </li>
                  <li>For each statement, indicate how accurate it is:</li>
                </ul>
                <div className="personality-scale-preview modal-scale">
                  <span className="scale-item">(1) Strongly Disagree</span>
                  <span className="scale-item">(2) Disagree</span>
                  <span className="scale-item">(3) Neither</span>
                  <span className="scale-item">(4) Agree</span>
                  <span className="scale-item">(5) Strongly Agree</span>
                </div>
                <ul>
                  <li>
                    It will take most people around{" "}
                    <strong>10-15 minutes</strong> to complete
                  </li>
                  <li>There are no right or wrong answers</li>
                  <li>Answer honestly for the most accurate results</li>
                </ul>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>
                  Be yourself and answer honestly. Your responses help us
                  understand you better!
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowPersonalityTestModal(false);
                  startPersonalityTest();
                }}
              >
                I'm Ready, Let's Start! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Abstract Test Instructions Modal */}
      {showAbstractTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiBook className="modal-icon" />
              <h2>Abstract Reasoning Test</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message">
                <FiClock className="no-timer-icon" />
                <p className="no-timer-text">
                  Don't worry, there is no timer in this test!
                </p>
              </div>
              <div className="modal-intro">
                <p>
                  This test measures your{" "}
                  <strong>abstract reasoning ability</strong> - your capacity to
                  identify patterns and relationships between shapes and
                  figures.
                </p>
              </div>
              <div className="modal-instructions">
                <h4>Instructions:</h4>
                <ul>
                  <li>
                    Each question presents a sequence of shapes or patterns
                  </li>
                  <li>Identify the logical pattern or rule in the sequence</li>
                  <li>Select the answer that best completes the pattern</li>
                  <li>You must answer all questions to proceed</li>
                  <li>Take your time and analyze each pattern carefully</li>
                </ul>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>
                  Focus on the relationships between shapes. You've got this!
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowAbstractTestModal(false)}
              >
                I'm Ready, Let's Start! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Math Test Instructions Modal */}
      {showMathTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiBook className="modal-icon" />
              <h2>Mathematical Analogy Test</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message warning">
                <FiClock className="timer-icon" />
                <p className="timer-text">
                  This test has a 15-minute time limit!
                </p>
              </div>
              <div className="modal-intro">
                <p>
                  This test measures your{" "}
                  <strong>mathematical reasoning</strong> and ability to
                  calculate time differences accurately.
                </p>
              </div>
              <div className="modal-instructions">
                <h4>Instructions:</h4>
                <ul>
                  <li>You will be given TIME IN and TIME OUT values</li>
                  <li>
                    Calculate the total number of HOURS and MINUTES worked
                  </li>
                  <li>Also convert the time to TOTAL MINUTES</li>
                  <li>
                    <strong>You may use a calculator</strong> for this test
                  </li>
                  <li>Complete all rows before time runs out</li>
                </ul>
              </div>
              <div className="modal-example">
                <h4>Example:</h4>
                <p>
                  If TIME IN is <strong>8:00am</strong> and TIME OUT is{" "}
                  <strong>5:00pm</strong>:
                </p>
                <ul>
                  <li>
                    Total Hours: <strong>9 hours 0 minutes</strong>
                  </li>
                  <li>
                    Total Minutes: <strong>540 minutes</strong>
                  </li>
                </ul>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>
                  Take a deep breath, use your calculator, and work through each
                  row carefully!
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowMathTestModal(false);
                  startMathTest();
                }}
              >
                I'm Ready, Start the Timer! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Essay Writing Test Instructions Modal */}
      {showEssayTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiBook className="modal-icon" />
              <h2>Essay Writing Test</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message warning">
                <FiClock className="timer-icon" />
                <p className="timer-text">
                  This test has a 20-minute time limit!
                </p>
              </div>
              <div className="modal-intro">
                <p>You will write an essay on the following topic:</p>
                <div className="essay-topic-box">
                  <strong>
                    "How was your family upbringing affected your views in
                    life?"
                  </strong>
                </div>
              </div>
              <div className="modal-instructions">
                <h4>Requirements:</h4>
                <ul>
                  <li>
                    <strong>Minimum 200 words</strong> - Your essay must be at
                    least 200 words
                  </li>
                  <li>
                    <strong>20 minutes</strong> - You have 20 minutes to
                    complete your essay
                  </li>
                  <li>
                    Write honestly and thoughtfully about your personal
                    experiences
                  </li>
                  <li>
                    Focus on how your upbringing shaped your perspectives and
                    values
                  </li>
                  <li>The timer will start as soon as you begin</li>
                </ul>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>
                  Take your time, reflect on your experiences, and write from
                  the heart!
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowEssayTestModal(false);
                  startEssayTest();
                }}
              >
                I'm Ready, Start Writing! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Word Count Warning Modal */}
      {showWordCountWarning && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal word-count-warning-modal">
            <div className="iq-test-modal-header warning-header">
              <FiAlertCircle className="modal-icon warning-icon" />
              <h2>Word Count Warning</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="word-count-warning-content">
                <div className="warning-stats">
                  <span className="current-count">{essayWordCount}</span>
                  <span className="separator">/</span>
                  <span className="required-count">200</span>
                  <span className="words-label">words</span>
                </div>
                <p className="warning-message">
                  Your essay has only <strong>{essayWordCount} words</strong>,
                  which is below the recommended minimum of{" "}
                  <strong>200 words</strong>.
                </p>
                <p className="warning-note">
                  Submitting an essay below the minimum word count may affect
                  your application evaluation.
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer warning-footer">
              <button
                className="btn btn-outline"
                onClick={() => setShowWordCountWarning(false)}
              >
                <FiArrowLeft /> Continue Writing
              </button>
              <button className="btn btn-warning" onClick={confirmFinishEssay}>
                Submit Anyway <FiCheck />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Essay Timeout Modal */}
      {showEssayTimeoutModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal timeout-modal">
            <div className="iq-test-modal-header timeout-header">
              <FiClock className="modal-icon timeout-icon" />
              <h2>Time's Up!</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="timeout-content">
                <p className="timeout-message">
                  The <strong>20-minute time limit</strong> for the Essay
                  Writing Test has been reached.
                </p>
                <p className="timeout-note">
                  Your essay has been automatically submitted with{" "}
                  <strong>{essayWordCount} words</strong>.
                </p>
                {essayWordCount < 200 && (
                  <p className="timeout-warning">
                    Note: Your essay is below the recommended 200-word minimum,
                    which may affect your application evaluation.
                  </p>
                )}
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => setShowEssayTimeoutModal(false)}
              >
                I Understand <FiCheck />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Proficiency Test Instructions Modal */}
      {showExcelTestModal && (
        <div className="iq-test-modal-overlay">
          <div className="iq-test-modal">
            <div className="iq-test-modal-header">
              <FiFileText className="modal-icon" />
              <h2>Excel Proficiency Test</h2>
            </div>
            <div className="iq-test-modal-content">
              <div className="modal-message warning">
                <FiClock className="timer-icon" />
                <p className="timer-text">
                  This test has a 15-minute time limit!
                </p>
              </div>
              <div className="modal-intro">
                <p>
                  You will create a <strong>Client Billing Report</strong> table
                  in Microsoft Excel (or compatible spreadsheet software).
                </p>
              </div>
              <div className="modal-instructions">
                <h4>What You Need to Do:</h4>
                <ul>
                  <li>
                    Create a table with columns:{" "}
                    <strong>CLIENT, I, II, III, TOTAL</strong>
                  </li>
                  <li>
                    Enter data for 5 clients (A, B, C, D, E) with their values
                  </li>
                  <li>
                    The <strong>TOTAL column</strong> must use SUM formula to
                    add I + II + III for each row
                  </li>
                  <li>
                    Add a <strong>TOTAL row</strong> using SUM formula to total
                    each column
                  </li>
                  <li>
                    Save and upload your file (.xlsx, .xls, or .ods format)
                  </li>
                </ul>
              </div>
              <div className="modal-note">
                <FiAlertCircle className="note-icon" />
                <p>
                  Make sure you have Excel or a compatible spreadsheet
                  application ready before starting the test.
                </p>
              </div>
              <div className="modal-encouragement">
                <FiStar className="star-icon" />
                <p>
                  Show us your Excel skills - accuracy and proper formula usage
                  are key!
                </p>
              </div>
            </div>
            <div className="iq-test-modal-footer">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowExcelTestModal(false);
                  startExcelTest();
                }}
              >
                I'm Ready, Start Test! <FiArrowRight />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chatbot */}
      <Chatbot />
    </div>
  );
};

export default Membership;
